/**
 * Translation Service for SlimFile
 * Supports multiple languages with easy-to-use translation functions
 */

export type Language = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'zh' | 'ja' | 'ar' | 'hi' | 'ru';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
];

// Translation keys and their values for each language
export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    'header.home': 'Home',
    'header.features': 'Features',
    'header.pricing': 'Pricing',
    'header.blog': 'Blog',
    'header.login': 'Login',
    'header.signup': 'Sign Up',
    'header.dashboard': 'Dashboard',
    'header.logout': 'Logout',

    // Hero Section
    'hero.title': 'Compress. Convert. Collaborate.',
    'hero.subtitle': 'Your complete workspace for files and teams',
    'hero.cta': 'Get Started',
    'hero.learnMore': 'Learn More',

    // File Upload
    'upload.title': 'Upload Your Files',
    'upload.dragDrop': 'Drag and drop your files here',
    'upload.or': 'or',
    'upload.browse': 'Browse Files',
    'upload.support': 'Support for PDF, Images, PPTX, DOCX, and XLSX files up to 1GB',

    // Compression
    'compress.title': 'Compress Files',
    'compress.subtitle': 'Reduce file size without losing quality',
    'compress.start': 'Start Compressing',
    'compress.complete': 'Compression Complete!',
    'compress.downloading': 'Downloading...',
    'compress.download': 'Download',
    'compress.share': 'Share',
    'compress.reset': 'Compress More',
    'compress.heroTitle': 'Compress Files',
    'compress.heroSubtitle': 'Instantly',
    'compress.heroDescription': 'Reduce file sizes without compromising quality. Support for images, PDFs, and Office documents with',
    'compress.lightningFast': 'lightning-fast processing',
    'compress.feature.fast': 'Lightning Fast',
    'compress.feature.fastDesc': 'Compress in seconds',
    'compress.feature.secure': '100% Secure',
    'compress.feature.secureDesc': 'Client-side processing',
    'compress.feature.available': 'Always Available',
    'compress.feature.availableDesc': '24/7 compression',
    'compress.formatsTitle': 'Supported Compression Formats',
    'compress.imageCompression': 'Image Compression',
    'compress.documentCompression': 'Document Compression',
    'compress.howItWorks': 'How It Works',
    'compress.step1': 'Upload Files',
    'compress.step1Desc': 'Select or drag and drop your files',
    'compress.step2': 'Compress',
    'compress.step2Desc': 'We optimize your files instantly',
    'compress.step3': 'Download',
    'compress.step3Desc': 'Get your compressed files',
    'compress.uploadTitle': 'Upload Your Files',
    'compress.resultsTitle': 'Compression Results',
    'compress.uploadSubtitle': 'Drag and drop your files or click to browse',
    'compress.processingSubtitle': 'Your files are being compressed',
    'compress.whyChoose': 'Why Choose SlimFile?',
    'compress.quality': 'High Quality',
    'compress.qualityDesc': 'Maintain excellent quality while reducing file size',
    'compress.allTypes': 'All File Types',
    'compress.allTypesDesc': 'Support for images, PDFs, and PPTX files',
    'compress.batch': 'Batch Processing',
    'compress.batchDesc': 'Compress multiple files at once',
    'compress.noInstall': 'No Installation',
    'compress.noInstallDesc': 'Works directly in your browser',
    'compress.almostDone': 'Almost Done',
    'compress.hangInThere': 'Hang in there… finalizing compression!',
    'compress.notice': 'Compression Notice',
    'compress.failed': 'Compression Failed',
    'compress.failedDesc': 'There was an error compressing your file. Please try again.',
    'compress.sizeReduced': 'Size reduced by',
    'compress.loginSuccess': 'Login Successful',
    'compress.recompressPrompt': 'Please re-compress your files to download them.',

    // Conversion
    'convert.title': 'Convert Files',
    'convert.subtitle': 'Transform your files between formats',
    'convert.start': 'Start Converting',
    'convert.complete': 'Conversion Complete!',
    'convert.selectFormat': 'Select Format',
    'convert.convertTo': 'Convert to',
    'convert.heroTitle': 'Convert Files',
    'convert.heroSubtitle': 'Seamlessly',
    'convert.heroDescription': 'Transform your files between formats with perfect quality. Support for images, PDFs, and Office documents with',
    'convert.lightningFast': 'lightning-fast processing',
    'convert.feature.fast': 'Lightning Fast',
    'convert.feature.fastDesc': 'Convert in seconds',
    'convert.feature.secure': '100% Secure',
    'convert.feature.secureDesc': 'Client-side processing',
    'convert.feature.available': 'Always Available',
    'convert.feature.availableDesc': '24/7 conversion',
    'convert.conversionsTitle': 'Supported Conversions',
    'convert.imageConversions': 'Image Conversions',
    'convert.documentConversions': 'Document Conversions',
    'convert.howItWorks': 'How It Works',
    'convert.step1': 'Upload Files',
    'convert.step1Desc': 'Select or drag and drop your files',
    'convert.step2': 'Convert',
    'convert.step2Desc': 'We transform your files instantly',
    'convert.step3': 'Download',
    'convert.step3Desc': 'Get your converted files',
    'convert.uploadTitle': 'Upload Your Files',
    'convert.chooseFormat': 'Choose Output Format',
    'convert.resultsTitle': 'Conversion Results',
    'convert.uploadSubtitle': 'Drag and drop your files or click to browse',
    'convert.selectFormatSubtitle': 'Select the format you want to convert to',
    'convert.processingSubtitle': 'Your files are being converted',
    'convert.selectedFiles': 'Selected file',
    'convert.selectedFilesPlural': 'Selected files',
    'convert.convertToLabel': 'Convert to:',
    'convert.convertButton': 'Convert to',
    'convert.cancel': 'Cancel',
    'convert.whyChoose': 'Why Choose SlimFile?',
    'convert.quality': 'Perfect Quality',
    'convert.qualityDesc': 'Maintain excellent quality during conversion',
    'convert.allTypes': 'All File Types',
    'convert.allTypesDesc': 'Support for images, PDFs, and Office documents',
    'convert.batch': 'Batch Processing',
    'convert.batchDesc': 'Convert multiple files at once',
    'convert.noInstall': 'No Installation',
    'convert.noInstallDesc': 'Works directly in your browser',
    'convert.notice': 'Conversion Notice',
    'convert.failed': 'Conversion Failed',
    'convert.failedDesc': 'There was an error converting your file. Please try again.',
    'convert.successDesc': 'File converted successfully to',
    'convert.loginSuccess': 'Login Successful',
    'convert.reconvertPrompt': 'Please re-convert your files to download them.',

    // Convert & Compress
    'convertCompress.title': 'Convert & Compress',
    'convertCompress.subtitle': 'All in One',
    'convertCompress.heroDescription': 'Transform your files between formats and optimize their size in a single step. Perfect quality with',
    'convertCompress.maxEfficiency': 'maximum efficiency',
    'convertCompress.convertLabel': 'Convert',
    'convertCompress.compressLabel': 'Compress',
    'convertCompress.start': 'Start Processing',
    'convertCompress.feature.twoInOne': 'Two-in-One',
    'convertCompress.feature.twoInOneDesc': 'Convert and compress together',
    'convertCompress.feature.secure': '100% Secure',
    'convertCompress.feature.secureDesc': 'Client-side processing',
    'convertCompress.feature.timeSaver': 'Time Saver',
    'convertCompress.feature.timeSaverDesc': 'Single step processing',
    'convertCompress.formatsTitle': 'Supported Formats',
    'convertCompress.imageFormats': 'Image Formats',
    'convertCompress.documentFormats': 'Document Formats',
    'convertCompress.howItWorks': 'How It Works',
    'convertCompress.step1': 'Upload Files',
    'convertCompress.step1Desc': 'Select or drag and drop your files',
    'convertCompress.step2': 'Convert & Compress',
    'convertCompress.step2Desc': 'We transform and optimize your files',
    'convertCompress.step3': 'Download',
    'convertCompress.step3Desc': 'Get your converted and compressed files',
    'convertCompress.uploadTitle': 'Upload Your Files',
    'convertCompress.chooseFormat': 'Choose Output Format',
    'convertCompress.resultsTitle': 'Processing Results',
    'convertCompress.uploadSubtitle': 'Drag and drop your files or click to browse',
    'convertCompress.selectFormatSubtitle': 'Select the format you want to convert to',
    'convertCompress.processingSubtitle': 'Your files are being converted and compressed',
    'convertCompress.selectedFiles': 'Selected file',
    'convertCompress.selectedFilesPlural': 'Selected files',
    'convertCompress.convertToLabel': 'Convert to:',
    'convertCompress.processButton': 'Convert & Compress to',
    'convertCompress.cancel': 'Cancel',
    'convertCompress.whyChoose': 'Why Choose SlimFile?',
    'convertCompress.quality': 'Perfect Quality',
    'convertCompress.qualityDesc': 'Maintain excellent quality while converting and compressing',
    'convertCompress.efficiency': 'Maximum Efficiency',
    'convertCompress.efficiencyDesc': 'Get both conversion and compression in one step',
    'convertCompress.batch': 'Batch Processing',
    'convertCompress.batchDesc': 'Process multiple files simultaneously',
    'convertCompress.noInstall': 'No Installation',
    'convertCompress.noInstallDesc': 'Works directly in your browser',
    'convertCompress.converting': 'Converting...',
    'convertCompress.compressing': 'Compressing...',
    'convertCompress.complete': 'Complete',
    'convertCompress.notice': 'Processing Notice',
    'convertCompress.failed': 'Processing Failed',
    'convertCompress.failedDesc': 'There was an error processing your file. Please try again.',
    'convertCompress.successTitle': 'Processing Complete!',
    'convertCompress.successDesc': 'File converted to',
    'convertCompress.sizeReduced': 'and compressed. Size reduced by',
    'convertCompress.loginSuccess': 'Login Successful',
    'convertCompress.reprocessPrompt': 'Please re-process your files to download them.',

    // Common
    'common.processing': 'Processing...',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.loading': 'Loading...',
    'common.saveToCloud': 'Save to Cloud',

    // Footer
    'footer.about': 'About Us',
    'footer.contact': 'Contact',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.rights': 'All rights reserved',
    'footer.compressionTools': 'Compression Tools',
    'footer.compressPdf': 'Compress PDF Online',
    'footer.compressPptx': 'Compress PPTX Online',
    'footer.compressImages': 'Compress Images Online',
    'footer.compressPdfEmail': 'Compress PDF for Email',
    'footer.ourBlog': 'Our Blog Posts',
    'footer.viewAll': 'View All',
    'footer.pdfGuides': 'PDF Guides',
    'footer.imageOptimization': 'Image Optimization',
    'footer.documentMedia': 'Document & Media',
    'footer.performanceStrategy': 'Performance & Strategy',
    'footer.quickAccess': 'Quick Access',
    'footer.product': 'Product',
    'footer.compress': 'Compress',
    'footer.convertOnly': 'Convert Only',
    'footer.convertCompress': 'Convert and Compress',
    'footer.game': 'Game',
    'footer.features': 'Features',
    'footer.whyCompression': 'Why Compression?',
    'footer.company': 'Company',
    'footer.caseStudies': 'Case Studies',
    'footer.globalImpact': 'Global Impact',
    'footer.partnerships': 'Partnerships',
    'footer.sdgs': 'SDGs',
    'footer.ourBlogs': 'Our Blogs',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'API Dashboard',
    'footer.developerProgram': 'Developer Program',
    'footer.apiPricing': 'API Pricing',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Resources',
    'footer.documentation': 'Documentation',
    'footer.copyright': '© 2026 SlimFile. Compress • Convert • Collaborate',

    // FileUpload
    'fileUpload.supportText': 'Support for PDF, Images, PPTX, DOCX, and XLSX files up to 1GB',
    'fileUpload.dropFiles': 'Drop your files here or click to browse',
    'fileUpload.dragDropText': 'Drag and drop or click to select up to 10 files',
    'fileUpload.errorMaxFiles': 'You can upload up to 10 files at a time.',
    'fileUpload.errorInvalidType': 'Please select only PDF, JPEG, PNG, WebP, PPTX, DOCX, or XLSX files.',
    'fileUpload.errorOldPpt': 'Please save your presentation as a .pptx file before uploading.',
    'fileUpload.errorOldDoc': 'Please save your document as a .docx file before uploading.',
    'fileUpload.errorOldXls': 'Please save your spreadsheet as a .xlsx file before uploading.',
    'fileUpload.errorFileSize': 'Each file must be less than 1GB.',
  },

  es: {
    // Header
    'header.home': 'Inicio',
    'header.features': 'Características',
    'header.pricing': 'Precios',
    'header.blog': 'Blog',
    'header.login': 'Iniciar Sesión',
    'header.signup': 'Registrarse',
    'header.dashboard': 'Panel',
    'header.logout': 'Cerrar Sesión',

    // Hero Section
    'hero.title': 'Comprimir. Convertir. Simplificar',
    'hero.subtitle': 'Todas tus herramientas de archivos en un solo lugar',
    'hero.cta': 'Comenzar',
    'hero.learnMore': 'Aprende Más',

    // File Upload
    'upload.title': 'Sube Tus Archivos',
    'upload.dragDrop': 'Arrastra y suelta tus archivos aquí',
    'upload.or': 'o',
    'upload.browse': 'Explorar Archivos',
    'upload.support': 'Soporte para archivos PDF, Imágenes, PPTX, DOCX y XLSX hasta 1GB',

    // Compression
    'compress.title': 'Comprimir Archivos',
    'compress.subtitle': 'Reduce el tamaño sin perder calidad',
    'compress.start': 'Comenzar Compresión',
    'compress.complete': '¡Compresión Completa!',
    'compress.downloading': 'Descargando...',
    'compress.download': 'Descargar',
    'compress.share': 'Compartir',
    'compress.reset': 'Comprimir Más',
    'compress.heroTitle': 'Comprimir Archivos',
    'compress.heroSubtitle': 'Instantáneamente',
    'compress.heroDescription': 'Reduce el tamaño de los archivos sin comprometer la calidad. Soporte para imágenes, PDFs y documentos de Office con',
    'compress.lightningFast': 'procesamiento ultrarrápido',
    'compress.feature.fast': 'Ultrarrápido',
    'compress.feature.fastDesc': 'Comprime en segundos',
    'compress.feature.secure': '100% Seguro',
    'compress.feature.secureDesc': 'Procesamiento del lado del cliente',
    'compress.feature.available': 'Siempre Disponible',
    'compress.feature.availableDesc': 'Compresión 24/7',
    'compress.formatsTitle': 'Formatos de Compresión Compatibles',
    'compress.imageCompression': 'Compresión de Imágenes',
    'compress.documentCompression': 'Compresión de Documentos',
    'compress.howItWorks': 'Cómo Funciona',
    'compress.step1': 'Subir Archivos',
    'compress.step1Desc': 'Selecciona o arrastra y suelta tus archivos',
    'compress.step2': 'Comprimir',
    'compress.step2Desc': 'Optimizamos tus archivos al instante',
    'compress.step3': 'Descargar',
    'compress.step3Desc': 'Obtén tus archivos comprimidos',
    'compress.uploadTitle': 'Sube Tus Archivos',
    'compress.resultsTitle': 'Resultados de Compresión',
    'compress.uploadSubtitle': 'Arrastra y suelta tus archivos o haz clic para explorar',
    'compress.processingSubtitle': 'Tus archivos se están comprimiendo',
    'compress.whyChoose': '¿Por Qué Elegir SlimFile?',
    'compress.quality': 'Alta Calidad',
    'compress.qualityDesc': 'Mantén una excelente calidad mientras reduces el tamaño',
    'compress.allTypes': 'Todos los Tipos de Archivos',
    'compress.allTypesDesc': 'Soporte para imágenes, PDFs y archivos PPTX',
    'compress.batch': 'Procesamiento por Lotes',
    'compress.batchDesc': 'Comprime múltiples archivos a la vez',
    'compress.noInstall': 'Sin Instalación',
    'compress.noInstallDesc': 'Funciona directamente en tu navegador',
    'compress.almostDone': 'Casi Listo',
    'compress.hangInThere': '¡Aguanta! Finalizando la compresión...',
    'compress.notice': 'Aviso de Compresión',
    'compress.failed': 'Compresión Fallida',
    'compress.failedDesc': 'Hubo un error al comprimir tu archivo. Por favor, inténtalo de nuevo.',
    'compress.sizeReduced': 'Tamaño reducido en',
    'compress.loginSuccess': 'Inicio de Sesión Exitoso',
    'compress.recompressPrompt': 'Por favor, vuelve a comprimir tus archivos para descargarlos.',

    // Conversion
    'convert.title': 'Convertir Archivos',
    'convert.subtitle': 'Transforma tus archivos entre formatos',
    'convert.start': 'Comenzar Conversión',
    'convert.complete': '¡Conversión Completa!',
    'convert.selectFormat': 'Seleccionar Formato',
    'convert.convertTo': 'Convertir a',
    'convert.heroTitle': 'Convertir Archivos',
    'convert.heroSubtitle': 'Sin Problemas',
    'convert.heroDescription': 'Transforma tus archivos entre formatos con calidad perfecta. Soporte para imágenes, PDFs y documentos de Office con',
    'convert.converting': 'Convirtiendo...',
    'convert.successDesc': 'Archivo convertido exitosamente a',
    'convert.feature.fast': 'Ultrarrápido',
    'convert.feature.fastDesc': 'Convierte en segundos',
    'convert.feature.quality': 'Calidad Perfecta',
    'convert.feature.qualityDesc': 'Mantén excelente calidad durante la conversión',
    'convert.feature.secure': '100% Seguro',
    'convert.feature.secureDesc': 'Procesamiento del lado del cliente',
    'convert.formatsTitle': 'Conversiones Compatibles',
    'convert.images': 'Imágenes',
    'convert.documents': 'Documentos',
    'convert.howItWorks': 'Cómo Funciona',
    'convert.step1': 'Subir Archivos',
    'convert.step1Desc': 'Selecciona o arrastra y suelta tus archivos',
    'convert.step2': 'Convertir',
    'convert.step2Desc': 'Transformamos tus archivos al instante',
    'convert.step3': 'Descargar',
    'convert.step3Desc': 'Obtén tus archivos convertidos',
    'convert.whyChoose': '¿Por Qué Elegir SlimFile?',
    'convert.multiFormat': 'Multi-Formato',
    'convert.multiFormatDesc': 'Soporte para múltiples formatos de archivo',
    'convert.highQuality': 'Alta Calidad',
    'convert.highQualityDesc': 'Mantén excelente calidad durante la conversión',
    'convert.batchConvert': 'Conversión por Lotes',
    'convert.batchConvertDesc': 'Convierte múltiples archivos a la vez',
    'convert.noInstall': 'Sin Instalación',
    'convert.noInstallDesc': 'Funciona directamente en tu navegador',

    // Convert & Compress
    'convertCompress.heroTitle': 'Convertir y Comprimir',
    'convertCompress.heroSubtitle': 'Todo en Uno',
    'convertCompress.heroDescription': 'Transforma tus archivos entre formatos y optimiza su tamaño en un solo paso. Calidad perfecta con',
    'convertCompress.selectFormat': 'Seleccionar Formato',
    'convertCompress.convertTo': 'Convertir a',
    'convertCompress.converting': 'Convirtiendo...',
    'convertCompress.compressing': 'Comprimiendo...',
    'convertCompress.complete': 'Completo',
    'convertCompress.successDesc': 'Archivo convertido a',
    'convertCompress.feature.efficient': 'Eficiente',
    'convertCompress.feature.efficientDesc': 'Convierte y comprime juntos',
    'convertCompress.feature.quality': 'Calidad Perfecta',
    'convertCompress.feature.qualityDesc': 'Mantén excelente calidad al convertir y comprimir',
    'convertCompress.feature.timeSaving': 'Ahorro de Tiempo',
    'convertCompress.feature.timeSavingDesc': 'Procesamiento en un solo paso',
    'convertCompress.formatsTitle': 'Formatos Compatibles',
    'convertCompress.images': 'Imágenes',
    'convertCompress.documents': 'Documentos',
    'convertCompress.howItWorks': 'Cómo Funciona',
    'convertCompress.step1': 'Subir Archivos',
    'convertCompress.step1Desc': 'Selecciona o arrastra y suelta tus archivos',
    'convertCompress.step2': 'Convertir',
    'convertCompress.step2Desc': 'Transformamos tus archivos al formato deseado',
    'convertCompress.step3': 'Comprimir',
    'convertCompress.step3Desc': 'Optimizamos el tamaño del archivo',
    'convertCompress.step4': 'Descargar',
    'convertCompress.step4Desc': 'Obtén tus archivos convertidos y comprimidos',
    'convertCompress.whyChoose': '¿Por Qué Elegir SlimFile?',
    'convertCompress.twoInOne': 'Dos en Uno',
    'convertCompress.twoInOneDesc': 'Convierte y comprime en un solo paso',
    'convertCompress.maxCompression': 'Compresión Máxima',
    'convertCompress.maxCompressionDesc': 'Obtén el mejor tamaño de archivo posible',
    'convertCompress.smartOptimization': 'Optimización Inteligente',
    'convertCompress.smartOptimizationDesc': 'Algoritmos automáticos para mejores resultados',
    'convertCompress.oneClick': 'Un Clic',
    'convertCompress.oneClickDesc': 'Procesamiento simple y rápido',

    // Common
    'common.processing': 'Procesando...',
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.close': 'Cerrar',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.loading': 'Cargando...',
    'common.saveToCloud': 'Guardar en la Nube',

    // Footer
    'footer.about': 'Sobre Nosotros',
    'footer.contact': 'Contacto',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',
    'footer.rights': 'Todos los derechos reservados',
    'footer.compressionTools': 'Herramientas de Compresión',
    'footer.compressPdf': 'Comprimir PDF en Línea',
    'footer.compressPptx': 'Comprimir PPTX en Línea',
    'footer.compressImages': 'Comprimir Imágenes en Línea',
    'footer.compressPdfEmail': 'Comprimir PDF para Correo',
    'footer.ourBlog': 'Nuestras Publicaciones de Blog',
    'footer.viewAll': 'Ver Todo',
    'footer.pdfGuides': 'Guías de PDF',
    'footer.imageOptimization': 'Optimización de Imágenes',
    'footer.documentMedia': 'Documentos y Medios',
    'footer.performanceStrategy': 'Rendimiento y Estrategia',
    'footer.quickAccess': 'Acceso Rápido',
    'footer.product': 'Producto',
    'footer.compress': 'Comprimir',
    'footer.convertOnly': 'Solo Convertir',
    'footer.convertCompress': 'Convertir y Comprimir',
    'footer.game': 'Juego',
    'footer.features': 'Características',
    'footer.whyCompression': '¿Por Qué Compresión?',
    'footer.company': 'Empresa',
    'footer.caseStudies': 'Casos de Estudio',
    'footer.globalImpact': 'Impacto Global',
    'footer.partnerships': 'Asociaciones',
    'footer.sdgs': 'ODS',
    'footer.ourBlogs': 'Nuestros Blogs',
    'footer.slimfileApi': 'API de SlimFile',
    'footer.apiDashboard': 'Panel de API',
    'footer.developerProgram': 'Programa para Desarrolladores',
    'footer.apiPricing': 'Precios de API',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Recursos',
    'footer.documentation': 'Documentación',
    'footer.copyright': '© 2025 SlimFile. Comprimir • Convertir • Simplificar',

    // FileUpload
    'fileUpload.supportText': 'Soporte para archivos PDF, Imágenes, PPTX, DOCX y XLSX hasta 1GB',
    'fileUpload.dropFiles': 'Suelta tus archivos aquí o haz clic para explorar',
    'fileUpload.dragDropText': 'Arrastra y suelta o haz clic para seleccionar hasta 10 archivos',
    'fileUpload.errorMaxFiles': 'Puedes subir hasta 10 archivos a la vez.',
    'fileUpload.errorInvalidType': 'Por favor, selecciona solo archivos PDF, JPEG, PNG, WebP, PPTX, DOCX o XLSX.',
    'fileUpload.errorOldPpt': 'Por favor, guarda tu presentación como archivo .pptx antes de subirla.',
    'fileUpload.errorOldDoc': 'Por favor, guarda tu documento como archivo .docx antes de subirlo.',
    'fileUpload.errorOldXls': 'Por favor, guarda tu hoja de cálculo como archivo .xlsx antes de subirla.',
    'fileUpload.errorFileSize': 'Cada archivo debe ser menor de 1GB.',
  },

  fr: {
    // Header
    'header.home': 'Accueil',
    'header.features': 'Fonctionnalités',
    'header.pricing': 'Tarifs',
    'header.blog': 'Blog',
    'header.login': 'Connexion',
    'header.signup': 'S\'inscrire',
    'header.dashboard': 'Tableau de Bord',
    'header.logout': 'Déconnexion',

    // Hero Section
    'hero.title': 'Compresser. Convertir. Simplifier',
    'hero.subtitle': 'Tous vos outils de fichiers en un seul endroit',
    'hero.cta': 'Commencer',
    'hero.learnMore': 'En Savoir Plus',

    // File Upload
    'upload.title': 'Téléchargez Vos Fichiers',
    'upload.dragDrop': 'Glissez-déposez vos fichiers ici',
    'upload.or': 'ou',
    'upload.browse': 'Parcourir les Fichiers',
    'upload.support': 'Support pour les fichiers PDF, Images, PPTX, DOCX et XLSX jusqu\'à 1GB',

    // Compression
    'compress.title': 'Compresser les Fichiers',
    'compress.subtitle': 'Réduire la taille sans perte de qualité',
    'compress.start': 'Commencer la Compression',
    'compress.complete': 'Compression Terminée!',
    'compress.downloading': 'Téléchargement...',
    'compress.download': 'Télécharger',
    'compress.share': 'Partager',
    'compress.reset': 'Compresser Plus',
    'compress.heroTitle': 'Compresser les Fichiers',
    'compress.heroSubtitle': 'Instantanément',
    'compress.heroDescription': 'Réduisez la taille des fichiers sans compromettre la qualité. Support pour les images, PDFs et documents Office avec',
    'compress.lightningFast': 'traitement ultra-rapide',
    'compress.feature.fast': 'Ultra-Rapide',
    'compress.feature.fastDesc': 'Compressez en quelques secondes',
    'compress.feature.secure': '100% Sécurisé',
    'compress.feature.secureDesc': 'Traitement côté client',
    'compress.feature.available': 'Toujours Disponible',
    'compress.feature.availableDesc': 'Compression 24/7',
    'compress.formatsTitle': 'Formats de Compression Pris en Charge',
    'compress.imageCompression': 'Compression d\'Images',
    'compress.documentCompression': 'Compression de Documents',
    'compress.howItWorks': 'Comment Ça Marche',
    'compress.step1': 'Télécharger les Fichiers',
    'compress.step1Desc': 'Sélectionnez ou glissez-déposez vos fichiers',
    'compress.step2': 'Compresser',
    'compress.step2Desc': 'Nous optimisons vos fichiers instantanément',
    'compress.step3': 'Télécharger',
    'compress.step3Desc': 'Obtenez vos fichiers compressés',
    'compress.uploadTitle': 'Téléchargez Vos Fichiers',
    'compress.resultsTitle': 'Résultats de la Compression',
    'compress.uploadSubtitle': 'Glissez-déposez vos fichiers ou cliquez pour parcourir',
    'compress.processingSubtitle': 'Vos fichiers sont en cours de compression',
    'compress.whyChoose': 'Pourquoi Choisir SlimFile?',
    'compress.quality': 'Haute Qualité',
    'compress.qualityDesc': 'Maintenez une excellente qualité tout en réduisant la taille',
    'compress.allTypes': 'Tous les Types de Fichiers',
    'compress.allTypesDesc': 'Support pour les images, PDFs et fichiers PPTX',
    'compress.batch': 'Traitement par Lots',
    'compress.batchDesc': 'Compressez plusieurs fichiers à la fois',
    'compress.noInstall': 'Aucune Installation',
    'compress.noInstallDesc': 'Fonctionne directement dans votre navigateur',
    'compress.almostDone': 'Presque Terminé',
    'compress.hangInThere': 'Patientez... finalisation de la compression!',
    'compress.notice': 'Avis de Compression',
    'compress.failed': 'Compression Échouée',
    'compress.failedDesc': 'Une erreur s\'est produite lors de la compression de votre fichier. Veuillez réessayer.',
    'compress.sizeReduced': 'Taille réduite de',
    'compress.loginSuccess': 'Connexion Réussie',
    'compress.recompressPrompt': 'Veuillez re-compresser vos fichiers pour les télécharger.',

    // Conversion
    'convert.title': 'Convertir les Fichiers',
    'convert.subtitle': 'Transformez vos fichiers entre formats',
    'convert.start': 'Commencer la Conversion',
    'convert.complete': 'Conversion Terminée!',
    'convert.selectFormat': 'Sélectionner le Format',
    'convert.convertTo': 'Convertir en',
    'convert.heroTitle': 'Convertir les Fichiers',
    'convert.heroSubtitle': 'Sans Effort',
    'convert.heroDescription': 'Transformez vos fichiers entre formats avec une qualité parfaite. Support pour les images, PDFs et documents Office avec',
    'convert.converting': 'Conversion en cours...',
    'convert.successDesc': 'Fichier converti avec succès en',
    'convert.feature.fast': 'Ultra-Rapide',
    'convert.feature.fastDesc': 'Convertissez en quelques secondes',
    'convert.feature.quality': 'Qualité Parfaite',
    'convert.feature.qualityDesc': 'Maintenez une excellente qualité pendant la conversion',
    'convert.feature.secure': '100% Sécurisé',
    'convert.feature.secureDesc': 'Traitement côté client',
    'convert.formatsTitle': 'Conversions Prises en Charge',
    'convert.images': 'Images',
    'convert.documents': 'Documents',
    'convert.howItWorks': 'Comment Ça Marche',
    'convert.step1': 'Télécharger les Fichiers',
    'convert.step1Desc': 'Sélectionnez ou glissez-déposez vos fichiers',
    'convert.step2': 'Convertir',
    'convert.step2Desc': 'Nous transformons vos fichiers instantanément',
    'convert.step3': 'Télécharger',
    'convert.step3Desc': 'Obtenez vos fichiers convertis',
    'convert.whyChoose': 'Pourquoi Choisir SlimFile?',
    'convert.multiFormat': 'Multi-Format',
    'convert.multiFormatDesc': 'Support pour plusieurs formats de fichiers',
    'convert.highQuality': 'Haute Qualité',
    'convert.highQualityDesc': 'Maintenez une excellente qualité pendant la conversion',
    'convert.batchConvert': 'Conversion par Lots',
    'convert.batchConvertDesc': 'Convertissez plusieurs fichiers à la fois',
    'convert.noInstall': 'Aucune Installation',
    'convert.noInstallDesc': 'Fonctionne directement dans votre navigateur',

    // Convert & Compress
    'convertCompress.heroTitle': 'Convertir et Compresser',
    'convertCompress.heroSubtitle': 'Tout en Un',
    'convertCompress.heroDescription': 'Transformez vos fichiers entre formats et optimisez leur taille en une seule étape. Qualité parfaite avec',
    'convertCompress.selectFormat': 'Sélectionner le Format',
    'convertCompress.convertTo': 'Convertir en',
    'convertCompress.converting': 'Conversion en cours...',
    'convertCompress.compressing': 'Compression en cours...',
    'convertCompress.complete': 'Terminé',
    'convertCompress.successDesc': 'Fichier converti en',
    'convertCompress.feature.efficient': 'Efficace',
    'convertCompress.feature.efficientDesc': 'Convertissez et compressez ensemble',
    'convertCompress.feature.quality': 'Qualité Parfaite',
    'convertCompress.feature.qualityDesc': 'Maintenez une excellente qualité lors de la conversion et compression',
    'convertCompress.feature.timeSaving': 'Gain de Temps',
    'convertCompress.feature.timeSavingDesc': 'Traitement en une seule étape',
    'convertCompress.formatsTitle': 'Formats Pris en Charge',
    'convertCompress.images': 'Images',
    'convertCompress.documents': 'Documents',
    'convertCompress.howItWorks': 'Comment Ça Marche',
    'convertCompress.step1': 'Télécharger les Fichiers',
    'convertCompress.step1Desc': 'Sélectionnez ou glissez-déposez vos fichiers',
    'convertCompress.step2': 'Convertir',
    'convertCompress.step2Desc': 'Nous transformons vos fichiers au format souhaité',
    'convertCompress.step3': 'Compresser',
    'convertCompress.step3Desc': 'Nous optimisons la taille du fichier',
    'convertCompress.step4': 'Télécharger',
    'convertCompress.step4Desc': 'Obtenez vos fichiers convertis et compressés',
    'convertCompress.whyChoose': 'Pourquoi Choisir SlimFile?',
    'convertCompress.twoInOne': 'Deux en Un',
    'convertCompress.twoInOneDesc': 'Convertissez et compressez en une seule étape',
    'convertCompress.maxCompression': 'Compression Maximale',
    'convertCompress.maxCompressionDesc': 'Obtenez la meilleure taille de fichier possible',
    'convertCompress.smartOptimization': 'Optimisation Intelligente',
    'convertCompress.smartOptimizationDesc': 'Algorithmes automatiques pour de meilleurs résultats',
    'convertCompress.oneClick': 'Un Clic',
    'convertCompress.oneClickDesc': 'Traitement simple et rapide',

    // Common
    'common.processing': 'Traitement...',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.close': 'Fermer',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.loading': 'Chargement...',
    'common.saveToCloud': 'Enregistrer dans le Cloud',

    // Footer
    'footer.about': 'À Propos',
    'footer.contact': 'Contact',
    'footer.privacy': 'Politique de Confidentialité',
    'footer.terms': 'Conditions d\'Utilisation',
    'footer.rights': 'Tous droits réservés',
    'footer.compressionTools': 'Outils de Compression',
    'footer.compressPdf': 'Compresser PDF en Ligne',
    'footer.compressPptx': 'Compresser PPTX en Ligne',
    'footer.compressImages': 'Compresser Images en Ligne',
    'footer.compressPdfEmail': 'Compresser PDF pour Email',
    'footer.ourBlog': 'Nos Articles de Blog',
    'footer.viewAll': 'Voir Tout',
    'footer.pdfGuides': 'Guides PDF',
    'footer.imageOptimization': 'Optimisation d\'Images',
    'footer.documentMedia': 'Documents et Médias',
    'footer.performanceStrategy': 'Performance et Stratégie',
    'footer.quickAccess': 'Accès Rapide',
    'footer.product': 'Produit',
    'footer.compress': 'Compresser',
    'footer.convertOnly': 'Convertir Uniquement',
    'footer.convertCompress': 'Convertir et Compresser',
    'footer.game': 'Jeu',
    'footer.features': 'Fonctionnalités',
    'footer.whyCompression': 'Pourquoi la Compression?',
    'footer.company': 'Entreprise',
    'footer.caseStudies': 'Études de Cas',
    'footer.globalImpact': 'Impact Global',
    'footer.partnerships': 'Partenariats',
    'footer.sdgs': 'ODD',
    'footer.ourBlogs': 'Nos Blogs',
    'footer.slimfileApi': 'API SlimFile',
    'footer.apiDashboard': 'Tableau de Bord API',
    'footer.developerProgram': 'Programme Développeur',
    'footer.apiPricing': 'Tarification API',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Ressources',
    'footer.documentation': 'Documentation',
    'footer.copyright': '© 2025 SlimFile. Compresser • Convertir • Simplifier',

    // FileUpload
    'fileUpload.supportText': 'Support pour les fichiers PDF, Images, PPTX, DOCX et XLSX jusqu\'à 1GB',
    'fileUpload.dropFiles': 'Déposez vos fichiers ici ou cliquez pour parcourir',
    'fileUpload.dragDropText': 'Glissez-déposez ou cliquez pour sélectionner jusqu\'à 10 fichiers',
    'fileUpload.errorMaxFiles': 'Vous pouvez télécharger jusqu\'à 10 fichiers à la fois.',
    'fileUpload.errorInvalidType': 'Veuillez sélectionner uniquement des fichiers PDF, JPEG, PNG, WebP, PPTX, DOCX ou XLSX.',
    'fileUpload.errorOldPpt': 'Veuillez enregistrer votre présentation en tant que fichier .pptx avant de la télécharger.',
    'fileUpload.errorOldDoc': 'Veuillez enregistrer votre document en tant que fichier .docx avant de le télécharger.',
    'fileUpload.errorOldXls': 'Veuillez enregistrer votre feuille de calcul en tant que fichier .xlsx avant de la télécharger.',
    'fileUpload.errorFileSize': 'Chaque fichier doit faire moins de 1GB.',
  },

  de: {
    // Header
    'header.home': 'Startseite',
    'header.features': 'Funktionen',
    'header.pricing': 'Preise',
    'header.blog': 'Blog',
    'header.login': 'Anmelden',
    'header.signup': 'Registrieren',
    'header.dashboard': 'Dashboard',
    'header.logout': 'Abmelden',

    // Hero Section
    'hero.title': 'Komprimieren. Konvertieren. Vereinfachen',
    'hero.subtitle': 'Alle Ihre Datei-Tools an einem Ort',
    'hero.cta': 'Loslegen',
    'hero.learnMore': 'Mehr Erfahren',

    // File Upload
    'upload.title': 'Laden Sie Ihre Dateien Hoch',
    'upload.dragDrop': 'Ziehen Sie Ihre Dateien hierher',
    'upload.or': 'oder',
    'upload.browse': 'Dateien Durchsuchen',
    'upload.support': 'Unterstützung für PDF, Bilder, PPTX, DOCX und XLSX Dateien bis 1GB',

    // Compression
    'compress.title': 'Dateien Komprimieren',
    'compress.subtitle': 'Dateigröße ohne Qualitätsverlust reduzieren',
    'compress.start': 'Komprimierung Starten',
    'compress.complete': 'Komprimierung Abgeschlossen!',
    'compress.downloading': 'Wird heruntergeladen...',
    'compress.download': 'Herunterladen',
    'compress.share': 'Teilen',
    'compress.reset': 'Mehr Komprimieren',
    'compress.heroTitle': 'Dateien Komprimieren',
    'compress.heroSubtitle': 'Sofort',
    'compress.heroDescription': 'Reduzieren Sie Dateigrößen ohne Qualitätseinbußen. Unterstützung für Bilder, PDFs und Office-Dokumente mit',
    'compress.lightningFast': 'blitzschneller Verarbeitung',
    'compress.feature.fast': 'Blitzschnell',
    'compress.feature.fastDesc': 'Komprimieren in Sekunden',
    'compress.feature.secure': '100% Sicher',
    'compress.feature.secureDesc': 'Clientseitige Verarbeitung',
    'compress.feature.available': 'Immer Verfügbar',
    'compress.feature.availableDesc': '24/7 Komprimierung',
    'compress.formatsTitle': 'Unterstützte Komprimierungsformate',
    'compress.imageCompression': 'Bildkomprimierung',
    'compress.documentCompression': 'Dokumentenkomprimierung',
    'compress.howItWorks': 'So Funktioniert Es',
    'compress.step1': 'Dateien Hochladen',
    'compress.step1Desc': 'Wählen oder ziehen Sie Ihre Dateien',
    'compress.step2': 'Komprimieren',
    'compress.step2Desc': 'Wir optimieren Ihre Dateien sofort',
    'compress.step3': 'Herunterladen',
    'compress.step3Desc': 'Erhalten Sie Ihre komprimierten Dateien',
    'compress.uploadTitle': 'Laden Sie Ihre Dateien Hoch',
    'compress.resultsTitle': 'Komprimierungsergebnisse',
    'compress.uploadSubtitle': 'Ziehen Sie Ihre Dateien oder klicken Sie zum Durchsuchen',
    'compress.processingSubtitle': 'Ihre Dateien werden komprimiert',
    'compress.whyChoose': 'Warum SlimFile?',
    'compress.quality': 'Hohe Qualität',
    'compress.qualityDesc': 'Behalten Sie exzellente Qualität bei reduzierter Dateigröße',
    'compress.allTypes': 'Alle Dateitypen',
    'compress.allTypesDesc': 'Unterstützung für Bilder, PDFs und PPTX-Dateien',
    'compress.batch': 'Stapelverarbeitung',
    'compress.batchDesc': 'Komprimieren Sie mehrere Dateien gleichzeitig',
    'compress.noInstall': 'Keine Installation',
    'compress.noInstallDesc': 'Funktioniert direkt in Ihrem Browser',
    'compress.almostDone': 'Fast Fertig',
    'compress.hangInThere': 'Noch einen Moment... Komprimierung wird abgeschlossen!',
    'compress.notice': 'Komprimierungshinweis',
    'compress.failed': 'Komprimierung Fehlgeschlagen',
    'compress.failedDesc': 'Beim Komprimieren Ihrer Datei ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    'compress.sizeReduced': 'Größe reduziert um',
    'compress.loginSuccess': 'Anmeldung Erfolgreich',
    'compress.recompressPrompt': 'Bitte komprimieren Sie Ihre Dateien erneut, um sie herunterzuladen.',

    // Conversion
    'convert.title': 'Dateien Konvertieren',
    'convert.subtitle': 'Transformieren Sie Ihre Dateien zwischen Formaten',
    'convert.start': 'Konvertierung Starten',
    'convert.complete': 'Konvertierung Abgeschlossen!',
    'convert.selectFormat': 'Format Auswählen',
    'convert.convertTo': 'Konvertieren zu',
    'convert.heroTitle': 'Dateien Konvertieren',
    'convert.heroSubtitle': 'Nahtlos',
    'convert.heroDescription': 'Transformieren Sie Ihre Dateien zwischen Formaten mit perfekter Qualität. Unterstützung für Bilder, PDFs und Office-Dokumente mit',
    'convert.converting': 'Konvertierung läuft...',
    'convert.successDesc': 'Datei erfolgreich konvertiert zu',
    'convert.feature.fast': 'Blitzschnell',
    'convert.feature.fastDesc': 'Konvertieren in Sekunden',
    'convert.feature.quality': 'Perfekte Qualität',
    'convert.feature.qualityDesc': 'Behalten Sie exzellente Qualität bei der Konvertierung',
    'convert.feature.secure': '100% Sicher',
    'convert.feature.secureDesc': 'Clientseitige Verarbeitung',
    'convert.formatsTitle': 'Unterstützte Konvertierungen',
    'convert.images': 'Bilder',
    'convert.documents': 'Dokumente',
    'convert.howItWorks': 'So Funktioniert Es',
    'convert.step1': 'Dateien Hochladen',
    'convert.step1Desc': 'Wählen oder ziehen Sie Ihre Dateien',
    'convert.step2': 'Konvertieren',
    'convert.step2Desc': 'Wir transformieren Ihre Dateien sofort',
    'convert.step3': 'Herunterladen',
    'convert.step3Desc': 'Erhalten Sie Ihre konvertierten Dateien',
    'convert.whyChoose': 'Warum SlimFile?',
    'convert.multiFormat': 'Multi-Format',
    'convert.multiFormatDesc': 'Unterstützung für mehrere Dateiformate',
    'convert.highQuality': 'Hohe Qualität',
    'convert.highQualityDesc': 'Behalten Sie exzellente Qualität bei der Konvertierung',
    'convert.batchConvert': 'Stapelkonvertierung',
    'convert.batchConvertDesc': 'Konvertieren Sie mehrere Dateien gleichzeitig',
    'convert.noInstall': 'Keine Installation',
    'convert.noInstallDesc': 'Funktioniert direkt in Ihrem Browser',

    // Convert & Compress
    'convertCompress.heroTitle': 'Konvertieren und Komprimieren',
    'convertCompress.heroSubtitle': 'Alles in Einem',
    'convertCompress.heroDescription': 'Transformieren Sie Ihre Dateien zwischen Formaten und optimieren Sie ihre Größe in einem Schritt. Perfekte Qualität mit',
    'convertCompress.selectFormat': 'Format Auswählen',
    'convertCompress.convertTo': 'Konvertieren zu',
    'convertCompress.converting': 'Konvertierung läuft...',
    'convertCompress.compressing': 'Komprimierung läuft...',
    'convertCompress.complete': 'Abgeschlossen',
    'convertCompress.successDesc': 'Datei konvertiert zu',
    'convertCompress.feature.efficient': 'Effizient',
    'convertCompress.feature.efficientDesc': 'Konvertieren und komprimieren zusammen',
    'convertCompress.feature.quality': 'Perfekte Qualität',
    'convertCompress.feature.qualityDesc': 'Behalten Sie exzellente Qualität beim Konvertieren und Komprimieren',
    'convertCompress.feature.timeSaving': 'Zeitsparend',
    'convertCompress.feature.timeSavingDesc': 'Verarbeitung in einem Schritt',
    'convertCompress.formatsTitle': 'Unterstützte Formate',
    'convertCompress.images': 'Bilder',
    'convertCompress.documents': 'Dokumente',
    'convertCompress.howItWorks': 'So Funktioniert Es',
    'convertCompress.step1': 'Dateien Hochladen',
    'convertCompress.step1Desc': 'Wählen oder ziehen Sie Ihre Dateien',
    'convertCompress.step2': 'Konvertieren',
    'convertCompress.step2Desc': 'Wir transformieren Ihre Dateien ins gewünschte Format',
    'convertCompress.step3': 'Komprimieren',
    'convertCompress.step3Desc': 'Wir optimieren die Dateigröße',
    'convertCompress.step4': 'Herunterladen',
    'convertCompress.step4Desc': 'Erhalten Sie Ihre konvertierten und komprimierten Dateien',
    'convertCompress.whyChoose': 'Warum SlimFile?',
    'convertCompress.twoInOne': 'Zwei in Einem',
    'convertCompress.twoInOneDesc': 'Konvertieren und komprimieren in einem Schritt',
    'convertCompress.maxCompression': 'Maximale Komprimierung',
    'convertCompress.maxCompressionDesc': 'Erhalten Sie die bestmögliche Dateigröße',
    'convertCompress.smartOptimization': 'Intelligente Optimierung',
    'convertCompress.smartOptimizationDesc': 'Automatische Algorithmen für beste Ergebnisse',
    'convertCompress.oneClick': 'Ein Klick',
    'convertCompress.oneClickDesc': 'Einfache und schnelle Verarbeitung',

    // Common
    'common.processing': 'Wird verarbeitet...',
    'common.cancel': 'Abbrechen',
    'common.save': 'Speichern',
    'common.close': 'Schließen',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.loading': 'Wird geladen...',
    'common.saveToCloud': 'In Cloud Speichern',

    // Footer
    'footer.about': 'Über Uns',
    'footer.contact': 'Kontakt',
    'footer.privacy': 'Datenschutzrichtlinie',
    'footer.terms': 'Nutzungsbedingungen',
    'footer.rights': 'Alle Rechte vorbehalten',
    'footer.compressionTools': 'Komprimierungswerkzeuge',
    'footer.compressPdf': 'PDF Online Komprimieren',
    'footer.compressPptx': 'PPTX Online Komprimieren',
    'footer.compressImages': 'Bilder Online Komprimieren',
    'footer.compressPdfEmail': 'PDF für E-Mail Komprimieren',
    'footer.ourBlog': 'Unsere Blog-Beiträge',
    'footer.viewAll': 'Alle Anzeigen',
    'footer.pdfGuides': 'PDF-Anleitungen',
    'footer.imageOptimization': 'Bildoptimierung',
    'footer.documentMedia': 'Dokumente & Medien',
    'footer.performanceStrategy': 'Leistung & Strategie',
    'footer.quickAccess': 'Schnellzugriff',
    'footer.product': 'Produkt',
    'footer.compress': 'Komprimieren',
    'footer.convertOnly': 'Nur Konvertieren',
    'footer.convertCompress': 'Konvertieren und Komprimieren',
    'footer.game': 'Spiel',
    'footer.features': 'Funktionen',
    'footer.whyCompression': 'Warum Komprimierung?',
    'footer.company': 'Unternehmen',
    'footer.caseStudies': 'Fallstudien',
    'footer.globalImpact': 'Globale Wirkung',
    'footer.partnerships': 'Partnerschaften',
    'footer.sdgs': 'SDGs',
    'footer.ourBlogs': 'Unsere Blogs',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'API Dashboard',
    'footer.developerProgram': 'Entwicklerprogramm',
    'footer.apiPricing': 'API-Preise',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Ressourcen',
    'footer.documentation': 'Dokumentation',
    'footer.copyright': '© 2025 SlimFile. Komprimieren • Konvertieren • Vereinfachen',

    // FileUpload
    'fileUpload.supportText': 'Unterstützung für PDF, Bilder, PPTX, DOCX und XLSX Dateien bis 1GB',
    'fileUpload.dropFiles': 'Legen Sie Ihre Dateien hier ab oder klicken Sie zum Durchsuchen',
    'fileUpload.dragDropText': 'Ziehen und ablegen oder klicken Sie, um bis zu 10 Dateien auszuwählen',
    'fileUpload.errorMaxFiles': 'Sie können bis zu 10 Dateien gleichzeitig hochladen.',
    'fileUpload.errorInvalidType': 'Bitte wählen Sie nur PDF-, JPEG-, PNG-, WebP-, PPTX-, DOCX- oder XLSX-Dateien aus.',
    'fileUpload.errorOldPpt': 'Bitte speichern Sie Ihre Präsentation als .pptx-Datei, bevor Sie sie hochladen.',
    'fileUpload.errorOldDoc': 'Bitte speichern Sie Ihr Dokument als .docx-Datei, bevor Sie es hochladen.',
    'fileUpload.errorOldXls': 'Bitte speichern Sie Ihre Tabelle als .xlsx-Datei, bevor Sie sie hochladen.',
    'fileUpload.errorFileSize': 'Jede Datei muss kleiner als 1GB sein.',
  },

  pt: {
    // Header
    'header.home': 'Início',
    'header.features': 'Recursos',
    'header.pricing': 'Preços',
    'header.blog': 'Blog',
    'header.login': 'Entrar',
    'header.signup': 'Cadastrar',
    'header.dashboard': 'Painel',
    'header.logout': 'Sair',

    // Hero Section
    'hero.title': 'Comprimir. Converter. Simplificar',
    'hero.subtitle': 'Todas as suas ferramentas de arquivo em um só lugar',
    'hero.cta': 'Começar',
    'hero.learnMore': 'Saiba Mais',

    // File Upload
    'upload.title': 'Envie Seus Arquivos',
    'upload.dragDrop': 'Arraste e solte seus arquivos aqui',
    'upload.or': 'ou',
    'upload.browse': 'Procurar Arquivos',
    'upload.support': 'Suporte para arquivos PDF, Imagens, PPTX, DOCX e XLSX até 1GB',

    // Compression
    'compress.title': 'Comprimir Arquivos',
    'compress.subtitle': 'Reduza o tamanho sem perder qualidade',
    'compress.start': 'Iniciar Compressão',
    'compress.complete': 'Compressão Concluída!',
    'compress.downloading': 'Baixando...',
    'compress.download': 'Baixar',
    'compress.share': 'Compartilhar',
    'compress.reset': 'Comprimir Mais',
    'compress.heroTitle': 'Comprimir Arquivos',
    'compress.heroSubtitle': 'Instantaneamente',
    'compress.heroDescription': 'Reduza o tamanho dos arquivos sem comprometer a qualidade. Suporte para imagens, PDFs e documentos Office com',
    'compress.lightningFast': 'processamento ultrarrápido',
    'compress.feature.fast': 'Ultrarrápido',
    'compress.feature.fastDesc': 'Comprima em segundos',
    'compress.feature.secure': '100% Seguro',
    'compress.feature.secureDesc': 'Processamento no lado do cliente',
    'compress.feature.available': 'Sempre Disponível',
    'compress.feature.availableDesc': 'Compressão 24/7',
    'compress.formatsTitle': 'Formatos de Compressão Suportados',
    'compress.imageCompression': 'Compressão de Imagens',
    'compress.documentCompression': 'Compressão de Documentos',
    'compress.howItWorks': 'Como Funciona',
    'compress.step1': 'Enviar Arquivos',
    'compress.step1Desc': 'Selecione ou arraste e solte seus arquivos',
    'compress.step2': 'Comprimir',
    'compress.step2Desc': 'Otimizamos seus arquivos instantaneamente',
    'compress.step3': 'Baixar',
    'compress.step3Desc': 'Obtenha seus arquivos comprimidos',
    'compress.uploadTitle': 'Envie Seus Arquivos',
    'compress.resultsTitle': 'Resultados da Compressão',
    'compress.uploadSubtitle': 'Arraste e solte seus arquivos ou clique para procurar',
    'compress.processingSubtitle': 'Seus arquivos estão sendo comprimidos',
    'compress.whyChoose': 'Por Que Escolher SlimFile?',
    'compress.quality': 'Alta Qualidade',
    'compress.qualityDesc': 'Mantenha excelente qualidade enquanto reduz o tamanho',
    'compress.allTypes': 'Todos os Tipos de Arquivos',
    'compress.allTypesDesc': 'Suporte para imagens, PDFs e arquivos PPTX',
    'compress.batch': 'Processamento em Lote',
    'compress.batchDesc': 'Comprima múltiplos arquivos de uma vez',
    'compress.noInstall': 'Sem Instalação',
    'compress.noInstallDesc': 'Funciona diretamente no seu navegador',
    'compress.almostDone': 'Quase Pronto',
    'compress.hangInThere': 'Aguarde... finalizando a compressão!',
    'compress.notice': 'Aviso de Compressão',
    'compress.failed': 'Compressão Falhou',
    'compress.failedDesc': 'Houve um erro ao comprimir seu arquivo. Por favor, tente novamente.',
    'compress.sizeReduced': 'Tamanho reduzido em',
    'compress.loginSuccess': 'Login Bem-sucedido',
    'compress.recompressPrompt': 'Por favor, recomprima seus arquivos para baixá-los.',

    // Conversion
    'convert.title': 'Converter Arquivos',
    'convert.subtitle': 'Transforme seus arquivos entre formatos',
    'convert.start': 'Iniciar Conversão',
    'convert.complete': 'Conversão Concluída!',
    'convert.selectFormat': 'Selecionar Formato',
    'convert.convertTo': 'Converter para',
    'convert.heroTitle': 'Converter Arquivos',
    'convert.heroSubtitle': 'Perfeitamente',
    'convert.heroDescription': 'Transforme seus arquivos entre formatos com qualidade perfeita. Suporte para imagens, PDFs e documentos Office com',
    'convert.converting': 'Convertendo...',
    'convert.successDesc': 'Arquivo convertido com sucesso para',
    'convert.feature.fast': 'Ultrarrápido',
    'convert.feature.fastDesc': 'Converta em segundos',
    'convert.feature.quality': 'Qualidade Perfeita',
    'convert.feature.qualityDesc': 'Mantenha excelente qualidade durante a conversão',
    'convert.feature.secure': '100% Seguro',
    'convert.feature.secureDesc': 'Processamento no lado do cliente',
    'convert.formatsTitle': 'Conversões Suportadas',
    'convert.images': 'Imagens',
    'convert.documents': 'Documentos',
    'convert.howItWorks': 'Como Funciona',
    'convert.step1': 'Enviar Arquivos',
    'convert.step1Desc': 'Selecione ou arraste e solte seus arquivos',
    'convert.step2': 'Converter',
    'convert.step2Desc': 'Transformamos seus arquivos instantaneamente',
    'convert.step3': 'Baixar',
    'convert.step3Desc': 'Obtenha seus arquivos convertidos',
    'convert.whyChoose': 'Por Que Escolher SlimFile?',
    'convert.multiFormat': 'Multi-Formato',
    'convert.multiFormatDesc': 'Suporte para múltiplos formatos de arquivo',
    'convert.highQuality': 'Alta Qualidade',
    'convert.highQualityDesc': 'Mantenha excelente qualidade durante a conversão',
    'convert.batchConvert': 'Conversão em Lote',
    'convert.batchConvertDesc': 'Converta múltiplos arquivos de uma vez',
    'convert.noInstall': 'Sem Instalação',
    'convert.noInstallDesc': 'Funciona diretamente no seu navegador',

    // Convert & Compress
    'convertCompress.heroTitle': 'Converter e Comprimir',
    'convertCompress.heroSubtitle': 'Tudo em Um',
    'convertCompress.heroDescription': 'Transforme seus arquivos entre formatos e otimize seu tamanho em um único passo. Qualidade perfeita com',
    'convertCompress.selectFormat': 'Selecionar Formato',
    'convertCompress.convertTo': 'Converter para',
    'convertCompress.converting': 'Convertendo...',
    'convertCompress.compressing': 'Comprimindo...',
    'convertCompress.complete': 'Completo',
    'convertCompress.successDesc': 'Arquivo convertido para',
    'convertCompress.feature.efficient': 'Eficiente',
    'convertCompress.feature.efficientDesc': 'Converta e comprima juntos',
    'convertCompress.feature.quality': 'Qualidade Perfeita',
    'convertCompress.feature.qualityDesc': 'Mantenha excelente qualidade ao converter e comprimir',
    'convertCompress.feature.timeSaving': 'Economia de Tempo',
    'convertCompress.feature.timeSavingDesc': 'Processamento em uma única etapa',
    'convertCompress.formatsTitle': 'Formatos Suportados',
    'convertCompress.images': 'Imagens',
    'convertCompress.documents': 'Documentos',
    'convertCompress.howItWorks': 'Como Funciona',
    'convertCompress.step1': 'Enviar Arquivos',
    'convertCompress.step1Desc': 'Selecione ou arraste e solte seus arquivos',
    'convertCompress.step2': 'Converter',
    'convertCompress.step2Desc': 'Transformamos seus arquivos para o formato desejado',
    'convertCompress.step3': 'Comprimir',
    'convertCompress.step3Desc': 'Otimizamos o tamanho do arquivo',
    'convertCompress.step4': 'Baixar',
    'convertCompress.step4Desc': 'Obtenha seus arquivos convertidos e comprimidos',
    'convertCompress.whyChoose': 'Por Que Escolher SlimFile?',
    'convertCompress.twoInOne': 'Dois em Um',
    'convertCompress.twoInOneDesc': 'Converta e comprima em um único passo',
    'convertCompress.maxCompression': 'Compressão Máxima',
    'convertCompress.maxCompressionDesc': 'Obtenha o melhor tamanho de arquivo possível',
    'convertCompress.smartOptimization': 'Otimização Inteligente',
    'convertCompress.smartOptimizationDesc': 'Algoritmos automáticos para melhores resultados',
    'convertCompress.oneClick': 'Um Clique',
    'convertCompress.oneClickDesc': 'Processamento simples e rápido',

    // Common
    'common.processing': 'Processando...',
    'common.cancel': 'Cancelar',
    'common.save': 'Salvar',
    'common.close': 'Fechar',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.loading': 'Carregando...',
    'common.saveToCloud': 'Salvar na Nuvem',

    // Footer
    'footer.about': 'Sobre Nós',
    'footer.contact': 'Contato',
    'footer.privacy': 'Política de Privacidade',
    'footer.terms': 'Termos de Serviço',
    'footer.rights': 'Todos os direitos reservados',
    'footer.compressionTools': 'Ferramentas de Compressão',
    'footer.compressPdf': 'Comprimir PDF Online',
    'footer.compressPptx': 'Comprimir PPTX Online',
    'footer.compressImages': 'Comprimir Imagens Online',
    'footer.compressPdfEmail': 'Comprimir PDF para Email',
    'footer.ourBlog': 'Nossas Postagens de Blog',
    'footer.viewAll': 'Ver Tudo',
    'footer.pdfGuides': 'Guias de PDF',
    'footer.imageOptimization': 'Otimização de Imagens',
    'footer.documentMedia': 'Documentos e Mídia',
    'footer.performanceStrategy': 'Desempenho e Estratégia',
    'footer.quickAccess': 'Acesso Rápido',
    'footer.product': 'Produto',
    'footer.compress': 'Comprimir',
    'footer.convertOnly': 'Apenas Converter',
    'footer.convertCompress': 'Converter e Comprimir',
    'footer.game': 'Jogo',
    'footer.features': 'Recursos',
    'footer.whyCompression': 'Por Que Compressão?',
    'footer.company': 'Empresa',
    'footer.caseStudies': 'Estudos de Caso',
    'footer.globalImpact': 'Impacto Global',
    'footer.partnerships': 'Parcerias',
    'footer.sdgs': 'ODS',
    'footer.ourBlogs': 'Nossos Blogs',
    'footer.slimfileApi': 'API SlimFile',
    'footer.apiDashboard': 'Painel API',
    'footer.developerProgram': 'Programa de Desenvolvedores',
    'footer.apiPricing': 'Preços da API',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Recursos',
    'footer.documentation': 'Documentação',
    'footer.copyright': '© 2025 SlimFile. Comprimir • Converter • Simplificar',

    // FileUpload
    'fileUpload.supportText': 'Suporte para arquivos PDF, Imagens, PPTX, DOCX e XLSX até 1GB',
    'fileUpload.dropFiles': 'Solte seus arquivos aqui ou clique para procurar',
    'fileUpload.dragDropText': 'Arraste e solte ou clique para selecionar até 10 arquivos',
    'fileUpload.errorMaxFiles': 'Você pode enviar até 10 arquivos de uma vez.',
    'fileUpload.errorInvalidType': 'Por favor, selecione apenas arquivos PDF, JPEG, PNG, WebP, PPTX, DOCX ou XLSX.',
    'fileUpload.errorOldPpt': 'Por favor, salve sua apresentação como arquivo .pptx antes de enviar.',
    'fileUpload.errorOldDoc': 'Por favor, salve seu documento como arquivo .docx antes de enviar.',
    'fileUpload.errorOldXls': 'Por favor, salve sua planilha como arquivo .xlsx antes de enviar.',
    'fileUpload.errorFileSize': 'Cada arquivo deve ter menos de 1GB.',
  },

  zh: {
    // Header
    'header.home': '首页',
    'header.features': '功能',
    'header.pricing': '价格',
    'header.blog': '博客',
    'header.login': '登录',
    'header.signup': '注册',
    'header.dashboard': '仪表板',
    'header.logout': '登出',

    // Hero Section
    'hero.title': '压缩。转换。简化',
    'hero.subtitle': '您所有的文件工具集于一处',
    'hero.cta': '开始使用',
    'hero.learnMore': '了解更多',

    // File Upload
    'upload.title': '上传您的文件',
    'upload.dragDrop': '拖放文件到这里',
    'upload.or': '或',
    'upload.browse': '浏览文件',
    'upload.support': '支持PDF、图片、PPTX、DOCX和XLSX文件，最大1GB',

    // Compression
    'compress.title': '压缩文件',
    'compress.subtitle': '在不损失质量的情况下减小文件大小',
    'compress.start': '开始压缩',
    'compress.complete': '压缩完成！',
    'compress.downloading': '下载中...',
    'compress.download': '下载',
    'compress.share': '分享',
    'compress.reset': '压缩更多',
    'compress.heroTitle': '压缩文件',
    'compress.heroSubtitle': '即时',
    'compress.heroDescription': '在不损失质量的情况下减小文件大小。支持图片、PDF和Office文档，拥有',
    'compress.lightningFast': '闪电般的处理速度',
    'compress.feature.fast': '闪电般快速',
    'compress.feature.fastDesc': '几秒内完成压缩',
    'compress.feature.secure': '100% 安全',
    'compress.feature.secureDesc': '客户端处理',
    'compress.feature.available': '随时可用',
    'compress.feature.availableDesc': '24/7 压缩服务',
    'compress.formatsTitle': '支持的压缩格式',
    'compress.imageCompression': '图片压缩',
    'compress.documentCompression': '文档压缩',
    'compress.howItWorks': '如何工作',
    'compress.step1': '上传文件',
    'compress.step1Desc': '选择或拖放您的文件',
    'compress.step2': '压缩',
    'compress.step2Desc': '我们即时优化您的文件',
    'compress.step3': '下载',
    'compress.step3Desc': '获取您的压缩文件',
    'compress.uploadTitle': '上传您的文件',
    'compress.resultsTitle': '压缩结果',
    'compress.uploadSubtitle': '拖放您的文件或点击浏览',
    'compress.processingSubtitle': '您的文件正在压缩中',
    'compress.whyChoose': '为什么选择SlimFile？',
    'compress.quality': '高质量',
    'compress.qualityDesc': '在减小文件大小的同时保持卓越质量',
    'compress.allTypes': '所有文件类型',
    'compress.allTypesDesc': '支持图片、PDF和PPTX文件',
    'compress.batch': '批量处理',
    'compress.batchDesc': '一次压缩多个文件',
    'compress.noInstall': '无需安装',
    'compress.noInstallDesc': '直接在浏览器中工作',
    'compress.almostDone': '快完成了',
    'compress.hangInThere': '请稍候... 正在完成压缩！',
    'compress.notice': '压缩通知',
    'compress.failed': '压缩失败',
    'compress.failedDesc': '压缩文件时出错。请重试。',
    'compress.sizeReduced': '大小减少了',
    'compress.loginSuccess': '登录成功',
    'compress.recompressPrompt': '请重新压缩您的文件以下载。',

    // Conversion
    'convert.title': '转换文件',
    'convert.subtitle': '在不同格式之间转换文件',
    'convert.start': '开始转换',
    'convert.complete': '转换完成！',
    'convert.selectFormat': '选择格式',
    'convert.convertTo': '转换为',
    'convert.heroTitle': '转换文件',
    'convert.heroSubtitle': '无缝',
    'convert.heroDescription': '以完美质量在格式之间转换文件。支持图片、PDF和Office文档，拥有',
    'convert.converting': '转换中...',
    'convert.successDesc': '文件成功转换为',
    'convert.feature.fast': '闪电般快速',
    'convert.feature.fastDesc': '几秒内完成转换',
    'convert.feature.quality': '完美质量',
    'convert.feature.qualityDesc': '转换过程中保持卓越质量',
    'convert.feature.secure': '100% 安全',
    'convert.feature.secureDesc': '客户端处理',
    'convert.formatsTitle': '支持的转换',
    'convert.images': '图片',
    'convert.documents': '文档',
    'convert.howItWorks': '如何工作',
    'convert.step1': '上传文件',
    'convert.step1Desc': '选择或拖放您的文件',
    'convert.step2': '转换',
    'convert.step2Desc': '我们即时转换您的文件',
    'convert.step3': '下载',
    'convert.step3Desc': '获取您的转换文件',
    'convert.whyChoose': '为什么选择SlimFile？',
    'convert.multiFormat': '多格式',
    'convert.multiFormatDesc': '支持多种文件格式',
    'convert.highQuality': '高质量',
    'convert.highQualityDesc': '转换过程中保持卓越质量',
    'convert.batchConvert': '批量转换',
    'convert.batchConvertDesc': '一次转换多个文件',
    'convert.noInstall': '无需安装',
    'convert.noInstallDesc': '直接在浏览器中工作',

    // Convert & Compress
    'convertCompress.heroTitle': '转换和压缩',
    'convertCompress.heroSubtitle': '一体化',
    'convertCompress.heroDescription': '一步完成格式转换和大小优化。完美质量与',
    'convertCompress.selectFormat': '选择格式',
    'convertCompress.convertTo': '转换为',
    'convertCompress.converting': '转换中...',
    'convertCompress.compressing': '压缩中...',
    'convertCompress.complete': '完成',
    'convertCompress.successDesc': '文件已转换为',
    'convertCompress.feature.efficient': '高效',
    'convertCompress.feature.efficientDesc': '一起转换和压缩',
    'convertCompress.feature.quality': '完美质量',
    'convertCompress.feature.qualityDesc': '转换和压缩时保持卓越质量',
    'convertCompress.feature.timeSaving': '节省时间',
    'convertCompress.feature.timeSavingDesc': '一步完成处理',
    'convertCompress.formatsTitle': '支持的格式',
    'convertCompress.images': '图片',
    'convertCompress.documents': '文档',
    'convertCompress.howItWorks': '如何工作',
    'convertCompress.step1': '上传文件',
    'convertCompress.step1Desc': '选择或拖放您的文件',
    'convertCompress.step2': '转换',
    'convertCompress.step2Desc': '我们将文件转换为所需格式',
    'convertCompress.step3': '压缩',
    'convertCompress.step3Desc': '我们优化文件大小',
    'convertCompress.step4': '下载',
    'convertCompress.step4Desc': '获取您转换和压缩的文件',
    'convertCompress.whyChoose': '为什么选择SlimFile？',
    'convertCompress.twoInOne': '二合一',
    'convertCompress.twoInOneDesc': '一步完成转换和压缩',
    'convertCompress.maxCompression': '最大压缩',
    'convertCompress.maxCompressionDesc': '获得最佳文件大小',
    'convertCompress.smartOptimization': '智能优化',
    'convertCompress.smartOptimizationDesc': '自动算法获得最佳效果',
    'convertCompress.oneClick': '一键操作',
    'convertCompress.oneClickDesc': '简单快速的处理',

    // Common
    'common.processing': '处理中...',
    'common.cancel': '取消',
    'common.save': '保存',
    'common.close': '关闭',
    'common.error': '错误',
    'common.success': '成功',
    'common.loading': '加载中...',
    'common.saveToCloud': '保存到云端',

    // Footer
    'footer.about': '关于我们',
    'footer.contact': '联系我们',
    'footer.privacy': '隐私政策',
    'footer.terms': '服务条款',
    'footer.rights': '版权所有',
    'footer.compressionTools': '压缩工具',
    'footer.compressPdf': '在线压缩PDF',
    'footer.compressPptx': '在线压缩PPTX',
    'footer.compressImages': '在线压缩图片',
    'footer.compressPdfEmail': '压缩PDF用于邮件',
    'footer.ourBlog': '我们的博客文章',
    'footer.viewAll': '查看全部',
    'footer.pdfGuides': 'PDF指南',
    'footer.imageOptimization': '图片优化',
    'footer.documentMedia': '文档和媒体',
    'footer.performanceStrategy': '性能与策略',
    'footer.quickAccess': '快速访问',
    'footer.product': '产品',
    'footer.compress': '压缩',
    'footer.convertOnly': '仅转换',
    'footer.convertCompress': '转换和压缩',
    'footer.game': '游戏',
    'footer.features': '功能',
    'footer.whyCompression': '为什么要压缩？',
    'footer.company': '公司',
    'footer.caseStudies': '案例研究',
    'footer.globalImpact': '全球影响',
    'footer.partnerships': '合作伙伴',
    'footer.sdgs': '可持续发展目标',
    'footer.ourBlogs': '我们的博客',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'API控制面板',
    'footer.developerProgram': '开发者计划',
    'footer.apiPricing': 'API定价',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': '资源',
    'footer.documentation': '文档',
    'footer.copyright': '© 2025 SlimFile. 压缩 • 转换 • 简化',

    // FileUpload
    'fileUpload.supportText': '支持PDF、图片、PPTX、DOCX和XLSX文件，最大1GB',
    'fileUpload.dropFiles': '将文件拖放到此处或点击浏览',
    'fileUpload.dragDropText': '拖放或点击选择最多10个文件',
    'fileUpload.errorMaxFiles': '您一次最多可以上传10个文件。',
    'fileUpload.errorInvalidType': '请只选择PDF、JPEG、PNG、WebP、PPTX、DOCX或XLSX文件。',
    'fileUpload.errorOldPpt': '请在上传前将演示文稿另存为.pptx文件。',
    'fileUpload.errorOldDoc': '请在上传前将文档另存为.docx文件。',
    'fileUpload.errorOldXls': '请在上传前将电子表格另存为.xlsx文件。',
    'fileUpload.errorFileSize': '每个文件必须小于1GB。',
  },

  ja: {
    // Header
    'header.home': 'ホーム',
    'header.features': '機能',
    'header.pricing': '価格',
    'header.blog': 'ブログ',
    'header.login': 'ログイン',
    'header.signup': '登録',
    'header.dashboard': 'ダッシュボード',
    'header.logout': 'ログアウト',

    // Hero Section
    'hero.title': '圧縮。変換。簡素化',
    'hero.subtitle': 'すべてのファイルツールを一箇所に',
    'hero.cta': '始める',
    'hero.learnMore': '詳しく見る',

    // File Upload
    'upload.title': 'ファイルをアップロード',
    'upload.dragDrop': 'ファイルをここにドラッグ＆ドロップ',
    'upload.or': 'または',
    'upload.browse': 'ファイルを参照',
    'upload.support': 'PDF、画像、PPTX、DOCX、XLSXファイルを最大1GBまでサポート',

    // Compression
    'compress.title': 'ファイルを圧縮',
    'compress.subtitle': '品質を損なわずにファイルサイズを削減',
    'compress.start': '圧縮を開始',
    'compress.complete': '圧縮完了！',
    'compress.downloading': 'ダウンロード中...',
    'compress.download': 'ダウンロード',
    'compress.share': '共有',
    'compress.reset': 'さらに圧縮',
    'compress.heroTitle': 'ファイルを圧縮',
    'compress.heroSubtitle': '即座に',
    'compress.heroDescription': '品質を損なわずにファイルサイズを削減。画像、PDF、Officeドキュメントをサポートし、',
    'compress.lightningFast': '超高速処理',
    'compress.feature.fast': '超高速',
    'compress.feature.fastDesc': '数秒で圧縮',
    'compress.feature.secure': '100%安全',
    'compress.feature.secureDesc': 'クライアント側処理',
    'compress.feature.available': '常に利用可能',
    'compress.feature.availableDesc': '24/7圧縮',
    'compress.formatsTitle': 'サポートされている圧縮形式',
    'compress.imageCompression': '画像圧縮',
    'compress.documentCompression': 'ドキュメント圧縮',
    'compress.howItWorks': '使い方',
    'compress.step1': 'ファイルをアップロード',
    'compress.step1Desc': 'ファイルを選択またはドラッグ＆ドロップ',
    'compress.step2': '圧縮',
    'compress.step2Desc': 'ファイルを即座に最適化',
    'compress.step3': 'ダウンロード',
    'compress.step3Desc': '圧縮されたファイルを取得',
    'compress.uploadTitle': 'ファイルをアップロード',
    'compress.resultsTitle': '圧縮結果',
    'compress.uploadSubtitle': 'ファイルをドラッグ＆ドロップまたはクリックして参照',
    'compress.processingSubtitle': 'ファイルを圧縮中',
    'compress.whyChoose': 'なぜSlimFileを選ぶのか？',
    'compress.quality': '高品質',
    'compress.qualityDesc': 'ファイルサイズを削減しながら優れた品質を維持',
    'compress.allTypes': 'すべてのファイルタイプ',
    'compress.allTypesDesc': '画像、PDF、PPTXファイルをサポート',
    'compress.batch': 'バッチ処理',
    'compress.batchDesc': '複数のファイルを一度に圧縮',
    'compress.noInstall': 'インストール不要',
    'compress.noInstallDesc': 'ブラウザで直接動作',
    'compress.almostDone': 'もうすぐ完了',
    'compress.hangInThere': 'お待ちください... 圧縮を完了しています！',
    'compress.notice': '圧縮通知',
    'compress.failed': '圧縮失敗',
    'compress.failedDesc': 'ファイルの圧縮中にエラーが発生しました。もう一度お試しください。',
    'compress.sizeReduced': 'サイズが削減されました',
    'compress.loginSuccess': 'ログイン成功',
    'compress.recompressPrompt': 'ダウンロードするにはファイルを再圧縮してください。',

    // Conversion
    'convert.title': 'ファイルを変換',
    'convert.subtitle': 'ファイルを異なる形式に変換',
    'convert.start': '変換を開始',
    'convert.complete': '変換完了！',
    'convert.selectFormat': 'フォーマットを選択',
    'convert.convertTo': '変換先',
    'convert.heroTitle': 'ファイルを変換',
    'convert.heroSubtitle': 'シームレスに',
    'convert.heroDescription': '完璧な品質でファイルを形式間で変換。画像、PDF、Officeドキュメントをサポートし、',
    'convert.converting': '変換中...',
    'convert.successDesc': 'ファイルが正常に変換されました',
    'convert.feature.fast': '超高速',
    'convert.feature.fastDesc': '数秒で変換',
    'convert.feature.quality': '完璧な品質',
    'convert.feature.qualityDesc': '変換中に優れた品質を維持',
    'convert.feature.secure': '100%安全',
    'convert.feature.secureDesc': 'クライアント側処理',
    'convert.formatsTitle': 'サポートされている変換',
    'convert.images': '画像',
    'convert.documents': 'ドキュメント',
    'convert.howItWorks': '使い方',
    'convert.step1': 'ファイルをアップロード',
    'convert.step1Desc': 'ファイルを選択またはドラッグ＆ドロップ',
    'convert.step2': '変換',
    'convert.step2Desc': 'ファイルを即座に変換',
    'convert.step3': 'ダウンロード',
    'convert.step3Desc': '変換されたファイルを取得',
    'convert.whyChoose': 'なぜSlimFileを選ぶのか？',
    'convert.multiFormat': 'マルチフォーマット',
    'convert.multiFormatDesc': '複数のファイル形式をサポート',
    'convert.highQuality': '高品質',
    'convert.highQualityDesc': '変換中に優れた品質を維持',
    'convert.batchConvert': 'バッチ変換',
    'convert.batchConvertDesc': '複数のファイルを一度に変換',
    'convert.noInstall': 'インストール不要',
    'convert.noInstallDesc': 'ブラウザで直接動作',

    // Convert & Compress
    'convertCompress.heroTitle': '変換と圧縮',
    'convertCompress.heroSubtitle': 'オールインワン',
    'convertCompress.heroDescription': 'ファイルを形式間で変換し、サイズを一度に最適化。完璧な品質で',
    'convertCompress.selectFormat': 'フォーマットを選択',
    'convertCompress.convertTo': '変換先',
    'convertCompress.converting': '変換中...',
    'convertCompress.compressing': '圧縮中...',
    'convertCompress.complete': '完了',
    'convertCompress.successDesc': 'ファイルが変換されました',
    'convertCompress.feature.efficient': '効率的',
    'convertCompress.feature.efficientDesc': '変換と圧縮を一緒に',
    'convertCompress.feature.quality': '完璧な品質',
    'convertCompress.feature.qualityDesc': '変換と圧縮中に優れた品質を維持',
    'convertCompress.feature.timeSaving': '時間節約',
    'convertCompress.feature.timeSavingDesc': '単一ステップの処理',
    'convertCompress.formatsTitle': 'サポートされている形式',
    'convertCompress.images': '画像',
    'convertCompress.documents': 'ドキュメント',
    'convertCompress.howItWorks': '使い方',
    'convertCompress.step1': 'ファイルをアップロード',
    'convertCompress.step1Desc': 'ファイルを選択またはドラッグ＆ドロップ',
    'convertCompress.step2': '変換',
    'convertCompress.step2Desc': 'ファイルを希望の形式に変換',
    'convertCompress.step3': '圧縮',
    'convertCompress.step3Desc': 'ファイルサイズを最適化',
    'convertCompress.step4': 'ダウンロード',
    'convertCompress.step4Desc': '変換および圧縮されたファイルを取得',
    'convertCompress.whyChoose': 'なぜSlimFileを選ぶのか？',
    'convertCompress.twoInOne': '2イン1',
    'convertCompress.twoInOneDesc': '一度に変換と圧縮',
    'convertCompress.maxCompression': '最大圧縮',
    'convertCompress.maxCompressionDesc': '最適なファイルサイズを実現',
    'convertCompress.smartOptimization': 'スマート最適化',
    'convertCompress.smartOptimizationDesc': '最適な結果のための自動アルゴリズム',
    'convertCompress.oneClick': 'ワンクリック',
    'convertCompress.oneClickDesc': 'シンプルで高速な処理',

    // Common
    'common.processing': '処理中...',
    'common.cancel': 'キャンセル',
    'common.save': '保存',
    'common.close': '閉じる',
    'common.error': 'エラー',
    'common.success': '成功',
    'common.loading': '読み込み中...',
    'common.saveToCloud': 'クラウドに保存',

    // Footer
    'footer.about': '会社概要',
    'footer.contact': 'お問い合わせ',
    'footer.privacy': 'プライバシーポリシー',
    'footer.terms': '利用規約',
    'footer.rights': '全著作権所有',
    'footer.compressionTools': '圧縮ツール',
    'footer.compressPdf': 'オンラインPDF圧縮',
    'footer.compressPptx': 'オンラインPPTX圧縮',
    'footer.compressImages': 'オンライン画像圧縮',
    'footer.compressPdfEmail': 'メール用PDF圧縮',
    'footer.ourBlog': 'ブログ記事',
    'footer.viewAll': 'すべて表示',
    'footer.pdfGuides': 'PDFガイド',
    'footer.imageOptimization': '画像最適化',
    'footer.documentMedia': 'ドキュメントとメディア',
    'footer.performanceStrategy': 'パフォーマンスと戦略',
    'footer.quickAccess': 'クイックアクセス',
    'footer.product': '製品',
    'footer.compress': '圧縮',
    'footer.convertOnly': '変換のみ',
    'footer.convertCompress': '変換と圧縮',
    'footer.game': 'ゲーム',
    'footer.features': '機能',
    'footer.whyCompression': 'なぜ圧縮？',
    'footer.company': '会社',
    'footer.caseStudies': 'ケーススタディ',
    'footer.globalImpact': 'グローバルインパクト',
    'footer.partnerships': 'パートナーシップ',
    'footer.sdgs': 'SDGs',
    'footer.ourBlogs': 'ブログ',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'APIダッシュボード',
    'footer.developerProgram': '開発者プログラム',
    'footer.apiPricing': 'API料金',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'リソース',
    'footer.documentation': 'ドキュメント',
    'footer.copyright': '© 2025 SlimFile. 圧縮 • 変換 • 簡素化',

    // FileUpload
    'fileUpload.supportText': 'PDF、画像、PPTX、DOCX、XLSXファイルを最大1GBまでサポート',
    'fileUpload.dropFiles': 'ファイルをここにドロップまたはクリックして参照',
    'fileUpload.dragDropText': 'ドラッグ＆ドロップまたはクリックして最大10ファイルを選択',
    'fileUpload.errorMaxFiles': '一度に最大10ファイルまでアップロードできます。',
    'fileUpload.errorInvalidType': 'PDF、JPEG、PNG、WebP、PPTX、DOCX、XLSXファイルのみを選択してください。',
    'fileUpload.errorOldPpt': 'アップロード前にプレゼンテーションを.pptxファイルとして保存してください。',
    'fileUpload.errorOldDoc': 'アップロード前にドキュメントを.docxファイルとして保存してください。',
    'fileUpload.errorOldXls': 'アップロード前にスプレッドシートを.xlsxファイルとして保存してください。',
    'fileUpload.errorFileSize': '各ファイルは1GB未満である必要があります。',
  },

  ar: {
    // Header
    'header.home': 'الرئيسية',
    'header.features': 'المميزات',
    'header.pricing': 'الأسعار',
    'header.blog': 'المدونة',
    'header.login': 'تسجيل الدخول',
    'header.signup': 'التسجيل',
    'header.dashboard': 'لوحة التحكم',
    'header.logout': 'تسجيل الخروج',

    // Hero Section
    'hero.title': 'ضغط. تحويل. تبسيط',
    'hero.subtitle': 'جميع أدوات الملفات في مكان واحد',
    'hero.cta': 'ابدأ الآن',
    'hero.learnMore': 'اعرف المزيد',

    // File Upload
    'upload.title': 'ارفع ملفاتك',
    'upload.dragDrop': 'اسحب وأفلت ملفاتك هنا',
    'upload.or': 'أو',
    'upload.browse': 'تصفح الملفات',
    'upload.support': 'دعم لملفات PDF والصور وPPTX وDOCX وXLSX حتى 200 ميجابايت',

    // Compression
    'compress.title': 'ضغط الملفات',
    'compress.subtitle': 'تقليل حجم الملف دون فقدان الجودة',
    'compress.start': 'بدء الضغط',
    'compress.complete': 'اكتمل الضغط!',
    'compress.downloading': 'جاري التنزيل...',
    'compress.download': 'تحميل',
    'compress.share': 'مشاركة',
    'compress.reset': 'ضغط المزيد',
    'compress.heroTitle': 'ضغط الملفات',
    'compress.heroSubtitle': 'فوراً',
    'compress.heroDescription': 'قلل حجم الملفات دون المساس بالجودة. دعم للصور وPDF ومستندات Office مع',
    'compress.lightningFast': 'معالجة فائقة السرعة',
    'compress.feature.fast': 'فائق السرعة',
    'compress.feature.fastDesc': 'ضغط في ثوانٍ',
    'compress.feature.secure': 'آمن 100٪',
    'compress.feature.secureDesc': 'معالجة من جانب العميل',
    'compress.feature.available': 'متاح دائماً',
    'compress.feature.availableDesc': 'ضغط 24/7',
    'compress.formatsTitle': 'تنسيقات الضغط المدعومة',
    'compress.imageCompression': 'ضغط الصور',
    'compress.documentCompression': 'ضغط المستندات',
    'compress.howItWorks': 'كيف يعمل',
    'compress.step1': 'رفع الملفات',
    'compress.step1Desc': 'اختر أو اسحب وأفلت ملفاتك',
    'compress.step2': 'ضغط',
    'compress.step2Desc': 'نحن نحسن ملفاتك على الفور',
    'compress.step3': 'تنزيل',
    'compress.step3Desc': 'احصل على ملفاتك المضغوطة',
    'compress.uploadTitle': 'ارفع ملفاتك',
    'compress.resultsTitle': 'نتائج الضغط',
    'compress.uploadSubtitle': 'اسحب وأفلت ملفاتك أو انقر للتصفح',
    'compress.processingSubtitle': 'جاري ضغط ملفاتك',
    'compress.whyChoose': 'لماذا تختار SlimFile؟',
    'compress.quality': 'جودة عالية',
    'compress.qualityDesc': 'حافظ على جودة ممتازة أثناء تقليل الحجم',
    'compress.allTypes': 'جميع أنواع الملفات',
    'compress.allTypesDesc': 'دعم للصور وPDF وملفات PPTX',
    'compress.batch': 'معالجة دفعية',
    'compress.batchDesc': 'اضغط عدة ملفات دفعة واحدة',
    'compress.noInstall': 'بدون تثبيت',
    'compress.noInstallDesc': 'يعمل مباشرة في متصفحك',
    'compress.almostDone': 'كاد أن ينتهي',
    'compress.hangInThere': 'انتظر قليلاً... إنهاء الضغط!',
    'compress.notice': 'إشعار الضغط',
    'compress.failed': 'فشل الضغط',
    'compress.failedDesc': 'حدث خطأ أثناء ضغط ملفك. يرجى المحاولة مرة أخرى.',
    'compress.sizeReduced': 'تم تقليل الحجم بنسبة',
    'compress.loginSuccess': 'تم تسجيل الدخول بنجاح',
    'compress.recompressPrompt': 'يرجى إعادة ضغط ملفاتك لتنزيلها.',

    // Conversion
    'convert.title': 'تحويل الملفات',
    'convert.subtitle': 'حول ملفاتك بين التنسيقات',
    'convert.start': 'بدء التحويل',
    'convert.complete': 'اكتمل التحويل!',
    'convert.selectFormat': 'اختر التنسيق',
    'convert.convertTo': 'تحويل إلى',
    'convert.heroTitle': 'تحويل الملفات',
    'convert.heroSubtitle': 'بسلاسة',
    'convert.heroDescription': 'حول ملفاتك بين التنسيقات بجودة مثالية. دعم للصور وPDF ومستندات Office مع',
    'convert.converting': 'جاري التحويل...',
    'convert.successDesc': 'تم تحويل الملف بنجاح إلى',
    'convert.feature.fast': 'فائق السرعة',
    'convert.feature.fastDesc': 'تحويل في ثوانٍ',
    'convert.feature.quality': 'جودة مثالية',
    'convert.feature.qualityDesc': 'حافظ على جودة ممتازة أثناء التحويل',
    'convert.feature.secure': 'آمن 100٪',
    'convert.feature.secureDesc': 'معالجة من جانب العميل',
    'convert.formatsTitle': 'التحويلات المدعومة',
    'convert.images': 'صور',
    'convert.documents': 'مستندات',
    'convert.howItWorks': 'كيف يعمل',
    'convert.step1': 'رفع الملفات',
    'convert.step1Desc': 'اختر أو اسحب وأفلت ملفاتك',
    'convert.step2': 'تحويل',
    'convert.step2Desc': 'نحن نحول ملفاتك على الفور',
    'convert.step3': 'تنزيل',
    'convert.step3Desc': 'احصل على ملفاتك المحولة',
    'convert.whyChoose': 'لماذا تختار SlimFile؟',
    'convert.multiFormat': 'متعدد التنسيقات',
    'convert.multiFormatDesc': 'دعم لتنسيقات ملفات متعددة',
    'convert.highQuality': 'جودة عالية',
    'convert.highQualityDesc': 'حافظ على جودة ممتازة أثناء التحويل',
    'convert.batchConvert': 'تحويل دفعي',
    'convert.batchConvertDesc': 'حول عدة ملفات دفعة واحدة',
    'convert.noInstall': 'بدون تثبيت',
    'convert.noInstallDesc': 'يعمل مباشرة في متصفحك',

    // Convert & Compress
    'convertCompress.heroTitle': 'تحويل وضغط',
    'convertCompress.heroSubtitle': 'الكل في واحد',
    'convertCompress.heroDescription': 'حول ملفاتك بين التنسيقات وحسّن حجمها في خطوة واحدة. جودة مثالية مع',
    'convertCompress.selectFormat': 'اختر التنسيق',
    'convertCompress.convertTo': 'تحويل إلى',
    'convertCompress.converting': 'جاري التحويل...',
    'convertCompress.compressing': 'جاري الضغط...',
    'convertCompress.complete': 'مكتمل',
    'convertCompress.successDesc': 'تم تحويل الملف إلى',
    'convertCompress.feature.efficient': 'فعال',
    'convertCompress.feature.efficientDesc': 'تحويل وضغط معاً',
    'convertCompress.feature.quality': 'جودة مثالية',
    'convertCompress.feature.qualityDesc': 'حافظ على جودة ممتازة أثناء التحويل والضغط',
    'convertCompress.feature.timeSaving': 'توفير الوقت',
    'convertCompress.feature.timeSavingDesc': 'معالجة في خطوة واحدة',
    'convertCompress.formatsTitle': 'التنسيقات المدعومة',
    'convertCompress.images': 'صور',
    'convertCompress.documents': 'مستندات',
    'convertCompress.howItWorks': 'كيف يعمل',
    'convertCompress.step1': 'رفع الملفات',
    'convertCompress.step1Desc': 'اختر أو اسحب وأفلت ملفاتك',
    'convertCompress.step2': 'تحويل',
    'convertCompress.step2Desc': 'نحن نحول ملفاتك إلى التنسيق المطلوب',
    'convertCompress.step3': 'ضغط',
    'convertCompress.step3Desc': 'نحن نحسن حجم الملف',
    'convertCompress.step4': 'تنزيل',
    'convertCompress.step4Desc': 'احصل على ملفاتك المحولة والمضغوطة',
    'convertCompress.whyChoose': 'لماذا تختار SlimFile؟',
    'convertCompress.twoInOne': 'اثنان في واحد',
    'convertCompress.twoInOneDesc': 'حول واضغط في خطوة واحدة',
    'convertCompress.maxCompression': 'ضغط أقصى',
    'convertCompress.maxCompressionDesc': 'احصل على أفضل حجم ممكن للملف',
    'convertCompress.smartOptimization': 'تحسين ذكي',
    'convertCompress.smartOptimizationDesc': 'خوارزميات تلقائية لأفضل النتائج',
    'convertCompress.oneClick': 'نقرة واحدة',
    'convertCompress.oneClickDesc': 'معالجة بسيطة وسريعة',

    // Common
    'common.processing': 'جاري المعالجة...',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.close': 'إغلاق',
    'common.error': 'خطأ',
    'common.success': 'نجح',
    'common.loading': 'جاري التحميل...',
    'common.saveToCloud': 'حفظ في السحابة',

    // Footer
    'footer.about': 'من نحن',
    'footer.contact': 'اتصل بنا',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'شروط الخدمة',
    'footer.rights': 'جميع الحقوق محفوظة',
    'footer.compressionTools': 'أدوات الضغط',
    'footer.compressPdf': 'ضغط PDF عبر الإنترنت',
    'footer.compressPptx': 'ضغط PPTX عبر الإنترنت',
    'footer.compressImages': 'ضغط الصور عبر الإنترنت',
    'footer.compressPdfEmail': 'ضغط PDF للبريد الإلكتروني',
    'footer.ourBlog': 'منشورات مدونتنا',
    'footer.viewAll': 'عرض الكل',
    'footer.pdfGuides': 'أدلة PDF',
    'footer.imageOptimization': 'تحسين الصور',
    'footer.documentMedia': 'الوثائق والوسائط',
    'footer.performanceStrategy': 'الأداء والاستراتيجية',
    'footer.quickAccess': 'وصول سريع',
    'footer.product': 'المنتج',
    'footer.compress': 'ضغط',
    'footer.convertOnly': 'تحويل فقط',
    'footer.convertCompress': 'تحويل وضغط',
    'footer.game': 'لعبة',
    'footer.features': 'المميزات',
    'footer.whyCompression': 'لماذا الضغط؟',
    'footer.company': 'الشركة',
    'footer.caseStudies': 'دراسات الحالة',
    'footer.globalImpact': 'التأثير العالمي',
    'footer.partnerships': 'الشراكات',
    'footer.sdgs': 'أهداف التنمية المستدامة',
    'footer.ourBlogs': 'مدوناتنا',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'لوحة تحكم API',
    'footer.developerProgram': 'برنامج المطورين',
    'footer.apiPricing': 'أسعار API',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'الموارد',
    'footer.documentation': 'التوثيق',
    'footer.copyright': '© 2025 SlimFile. ضغط • تحويل • تبسيط',

    // FileUpload
    'fileUpload.supportText': 'دعم لملفات PDF والصور وPPTX وDOCX وXLSX حتى 200 ميجابايت',
    'fileUpload.dropFiles': 'أفلت ملفاتك هنا أو انقر للتصفح',
    'fileUpload.dragDropText': 'اسحب وأفلت أو انقر لتحديد ما يصل إلى 10 ملفات',
    'fileUpload.errorMaxFiles': 'يمكنك تحميل ما يصل إلى 10 ملفات في المرة الواحدة.',
    'fileUpload.errorInvalidType': 'يرجى تحديد ملفات PDF أو JPEG أو PNG أو WebP أو PPTX أو DOCX أو XLSX فقط.',
    'fileUpload.errorOldPpt': 'يرجى حفظ عرضك التقديمي كملف .pptx قبل التحميل.',
    'fileUpload.errorOldDoc': 'يرجى حفظ مستندك كملف .docx قبل التحميل.',
    'fileUpload.errorOldXls': 'يرجى حفظ جدول البيانات كملف .xlsx قبل التحميل.',
    'fileUpload.errorFileSize': 'يجب أن يكون كل ملف أقل من 200 ميجابايت.',
  },

  hi: {
    // Header
    'header.home': 'होम',
    'header.features': 'फीचर्स',
    'header.pricing': 'मूल्य निर्धारण',
    'header.blog': 'ब्लॉग',
    'header.login': 'लॉग इन',
    'header.signup': 'साइन अप',
    'header.dashboard': 'डैशबोर्ड',
    'header.logout': 'लॉग आउट',

    // Hero Section
    'hero.title': 'संपीड़ित करें। परिवर्तित करें। सरल बनाएं',
    'hero.subtitle': 'आपके सभी फ़ाइल टूल एक जगह',
    'hero.cta': 'शुरू करें',
    'hero.learnMore': 'और जानें',

    // File Upload
    'upload.title': 'अपनी फ़ाइलें अपलोड करें',
    'upload.dragDrop': 'अपनी फ़ाइलों को यहाँ खींचें और छोड़ें',
    'upload.or': 'या',
    'upload.browse': 'फ़ाइलें ब्राउज़ करें',
    'upload.support': '1GB तक की PDF, इमेज, PPTX, DOCX और XLSX फ़ाइलों के लिए समर्थन',

    // Compression
    'compress.title': 'फ़ाइलें संपीड़ित करें',
    'compress.subtitle': 'गुणवत्ता खोए बिना फ़ाइल का आकार कम करें',
    'compress.start': 'संपीड़न शुरू करें',
    'compress.complete': 'संपीड़न पूर्ण!',
    'compress.downloading': 'डाउनलोड हो रहा है...',
    'compress.download': 'डाउनलोड',
    'compress.share': 'शेयर करें',
    'compress.reset': 'अधिक संपीड़ित करें',
    'compress.heroTitle': 'फ़ाइलें संपीड़ित करें',
    'compress.heroSubtitle': 'तुरंत',
    'compress.heroDescription': 'गुणवत्ता से समझौता किए बिना फ़ाइल का आकार कम करें। इमेज, PDF और Office दस्तावेज़ों के लिए समर्थन',
    'compress.lightningFast': 'बिजली-तेज़ प्रोसेसिंग',
    'compress.feature.fast': 'बिजली-तेज़',
    'compress.feature.fastDesc': 'सेकंडों में संपीड़ित करें',
    'compress.feature.secure': '100% सुरक्षित',
    'compress.feature.secureDesc': 'क्लाइंट-साइड प्रोसेसिंग',
    'compress.feature.available': 'हमेशा उपलब्ध',
    'compress.feature.availableDesc': '24/7 संपीड़न',
    'compress.formatsTitle': 'समर्थित संपीड़न प्रारूप',
    'compress.imageCompression': 'इमेज संपीड़न',
    'compress.documentCompression': 'दस्तावेज़ संपीड़न',
    'compress.howItWorks': 'यह कैसे काम करता है',
    'compress.step1': 'फ़ाइलें अपलोड करें',
    'compress.step1Desc': 'चुनें या अपनी फ़ाइलें खींचें और छोड़ें',
    'compress.step2': 'संपीड़ित करें',
    'compress.step2Desc': 'हम आपकी फ़ाइलों को तुरंत अनुकूलित करते हैं',
    'compress.step3': 'डाउनलोड करें',
    'compress.step3Desc': 'अपनी संपीड़ित फ़ाइलें प्राप्त करें',
    'compress.uploadTitle': 'अपनी फ़ाइलें अपलोड करें',
    'compress.resultsTitle': 'संपीड़न परिणाम',
    'compress.uploadSubtitle': 'अपनी फ़ाइलें खींचें और छोड़ें या ब्राउज़ करने के लिए क्लिक करें',
    'compress.processingSubtitle': 'आपकी फ़ाइलें संपीड़ित की जा रही हैं',
    'compress.whyChoose': 'SlimFile क्यों चुनें?',
    'compress.quality': 'उच्च गुणवत्ता',
    'compress.qualityDesc': 'आकार कम करते समय उत्कृष्ट गुणवत्ता बनाए रखें',
    'compress.allTypes': 'सभी फ़ाइल प्रकार',
    'compress.allTypesDesc': 'इमेज, PDF और PPTX फ़ाइलों के लिए समर्थन',
    'compress.batch': 'बैच प्रोसेसिंग',
    'compress.batchDesc': 'एक साथ कई फ़ाइलें संपीड़ित करें',
    'compress.noInstall': 'कोई इंस्टॉलेशन नहीं',
    'compress.noInstallDesc': 'सीधे आपके ब्राउज़र में काम करता है',
    'compress.almostDone': 'लगभग हो गया',
    'compress.hangInThere': 'थोड़ा रुकें... संपीड़न को अंतिम रूप दे रहे हैं!',
    'compress.notice': 'संपीड़न सूचना',
    'compress.failed': 'संपीड़न विफल',
    'compress.failedDesc': 'आपकी फ़ाइल को संपीड़ित करने में त्रुटि हुई। कृपया पुनः प्रयास करें।',
    'compress.sizeReduced': 'आकार कम हुआ',
    'compress.loginSuccess': 'लॉगिन सफल',
    'compress.recompressPrompt': 'कृपया डाउनलोड करने के लिए अपनी फ़ाइलों को फिर से संपीड़ित करें।',

    // Conversion
    'convert.title': 'फ़ाइलें परिवर्तित करें',
    'convert.subtitle': 'अपनी फ़ाइलों को प्रारूपों के बीच रूपांतरित करें',
    'convert.start': 'रूपांतरण शुरू करें',
    'convert.complete': 'रूपांतरण पूर्ण!',
    'convert.selectFormat': 'प्रारूप चुनें',
    'convert.convertTo': 'में परिवर्तित करें',
    'convert.heroTitle': 'फ़ाइलें परिवर्तित करें',
    'convert.heroSubtitle': 'सहजता से',
    'convert.heroDescription': 'अपनी फ़ाइलों को सही गुणवत्ता के साथ प्रारूपों के बीच परिवर्तित करें। इमेज, PDF और Office दस्तावेज़ों के लिए समर्थन',
    'convert.converting': 'परिवर्तित हो रहा है...',
    'convert.successDesc': 'फ़ाइल सफलतापूर्वक परिवर्तित हुई',
    'convert.feature.fast': 'बिजली-तेज़',
    'convert.feature.fastDesc': 'सेकंडों में परिवर्तित करें',
    'convert.feature.quality': 'सही गुणवत्ता',
    'convert.feature.qualityDesc': 'रूपांतरण के दौरान उत्कृष्ट गुणवत्ता बनाए रखें',
    'convert.feature.secure': '100% सुरक्षित',
    'convert.feature.secureDesc': 'क्लाइंट-साइड प्रोसेसिंग',
    'convert.formatsTitle': 'समर्थित रूपांतरण',
    'convert.images': 'इमेज',
    'convert.documents': 'दस्तावेज़',
    'convert.howItWorks': 'यह कैसे काम करता है',
    'convert.step1': 'फ़ाइलें अपलोड करें',
    'convert.step1Desc': 'चुनें या अपनी फ़ाइलें खींचें और छोड़ें',
    'convert.step2': 'परिवर्तित करें',
    'convert.step2Desc': 'हम आपकी फ़ाइलों को तुरंत रूपांतरित करते हैं',
    'convert.step3': 'डाउनलोड करें',
    'convert.step3Desc': 'अपनी परिवर्तित फ़ाइलें प्राप्त करें',
    'convert.whyChoose': 'SlimFile क्यों चुनें?',
    'convert.multiFormat': 'मल्टी-फॉर्मेट',
    'convert.multiFormatDesc': 'कई फ़ाइल प्रारूपों के लिए समर्थन',
    'convert.highQuality': 'उच्च गुणवत्ता',
    'convert.highQualityDesc': 'रूपांतरण के दौरान उत्कृष्ट गुणवत्ता बनाए रखें',
    'convert.batchConvert': 'बैच रूपांतरण',
    'convert.batchConvertDesc': 'एक साथ कई फ़ाइलें परिवर्तित करें',
    'convert.noInstall': 'कोई इंस्टॉलेशन नहीं',
    'convert.noInstallDesc': 'सीधे आपके ब्राउज़र में काम करता है',

    // Convert & Compress
    'convertCompress.heroTitle': 'परिवर्तित और संपीड़ित करें',
    'convertCompress.heroSubtitle': 'सब एक में',
    'convertCompress.heroDescription': 'अपनी फ़ाइलों को प्रारूपों के बीच परिवर्तित करें और एक ही चरण में उनके आकार को अनुकूलित करें। सही गुणवत्ता के साथ',
    'convertCompress.selectFormat': 'प्रारूप चुनें',
    'convertCompress.convertTo': 'में परिवर्तित करें',
    'convertCompress.converting': 'परिवर्तित हो रहा है...',
    'convertCompress.compressing': 'संपीड़ित हो रहा है...',
    'convertCompress.complete': 'पूर्ण',
    'convertCompress.successDesc': 'फ़ाइल परिवर्तित हुई',
    'convertCompress.feature.efficient': 'कुशल',
    'convertCompress.feature.efficientDesc': 'एक साथ परिवर्तित और संपीड़ित करें',
    'convertCompress.feature.quality': 'सही गुणवत्ता',
    'convertCompress.feature.qualityDesc': 'परिवर्तित और संपीड़ित करते समय उत्कृष्ट गुणवत्ता बनाए रखें',
    'convertCompress.feature.timeSaving': 'समय बचाएं',
    'convertCompress.feature.timeSavingDesc': 'एकल चरण प्रोसेसिंग',
    'convertCompress.formatsTitle': 'समर्थित प्रारूप',
    'convertCompress.images': 'इमेज',
    'convertCompress.documents': 'दस्तावेज़',
    'convertCompress.howItWorks': 'यह कैसे काम करता है',
    'convertCompress.step1': 'फ़ाइलें अपलोड करें',
    'convertCompress.step1Desc': 'चुनें या अपनी फ़ाइलें खींचें और छोड़ें',
    'convertCompress.step2': 'परिवर्तित करें',
    'convertCompress.step2Desc': 'हम आपकी फ़ाइलों को वांछित प्रारूप में परिवर्तित करते हैं',
    'convertCompress.step3': 'संपीड़ित करें',
    'convertCompress.step3Desc': 'हम फ़ाइल आकार को अनुकूलित करते हैं',
    'convertCompress.step4': 'डाउनलोड करें',
    'convertCompress.step4Desc': 'अपनी परिवर्तित और संपीड़ित फ़ाइलें प्राप्त करें',
    'convertCompress.whyChoose': 'SlimFile क्यों चुनें?',
    'convertCompress.twoInOne': 'दो-इन-वन',
    'convertCompress.twoInOneDesc': 'एक चरण में परिवर्तित और संपीड़ित करें',
    'convertCompress.maxCompression': 'अधिकतम संपीड़न',
    'convertCompress.maxCompressionDesc': 'सर्वोत्तम संभव फ़ाइल आकार प्राप्त करें',
    'convertCompress.smartOptimization': 'स्मार्ट अनुकूलन',
    'convertCompress.smartOptimizationDesc': 'बेहतर परिणामों के लिए स्वचालित एल्गोरिदम',
    'convertCompress.oneClick': 'एक क्लिक',
    'convertCompress.oneClickDesc': 'सरल और तेज़ प्रोसेसिंग',

    // Common
    'common.processing': 'प्रोसेसिंग...',
    'common.cancel': 'रद्द करें',
    'common.save': 'सहेजें',
    'common.close': 'बंद करें',
    'common.error': 'त्रुटि',
    'common.success': 'सफलता',
    'common.loading': 'लोड हो रहा है...',
    'common.saveToCloud': 'क्लाउड में सहेजें',

    // Footer
    'footer.about': 'हमारे बारे में',
    'footer.contact': 'संपर्क',
    'footer.privacy': 'गोपनीयता नीति',
    'footer.terms': 'सेवा की शर्तें',
    'footer.rights': 'सर्वाधिकार सुरक्षित',
    'footer.compressionTools': 'संपीड़न उपकरण',
    'footer.compressPdf': 'ऑनलाइन PDF संपीड़ित करें',
    'footer.compressPptx': 'ऑनलाइन PPTX संपीड़ित करें',
    'footer.compressImages': 'ऑनलाइन इमेज संपीड़ित करें',
    'footer.compressPdfEmail': 'ईमेल के लिए PDF संपीड़ित करें',
    'footer.ourBlog': 'हमारे ब्लॉग पोस्ट',
    'footer.viewAll': 'सभी देखें',
    'footer.pdfGuides': 'PDF गाइड',
    'footer.imageOptimization': 'इमेज अनुकूलन',
    'footer.documentMedia': 'दस्तावेज़ और मीडिया',
    'footer.performanceStrategy': 'प्रदर्शन और रणनीति',
    'footer.quickAccess': 'त्वरित पहुंच',
    'footer.product': 'उत्पाद',
    'footer.compress': 'संपीड़ित करें',
    'footer.convertOnly': 'केवल परिवर्तित करें',
    'footer.convertCompress': 'परिवर्तित और संपीड़ित करें',
    'footer.game': 'गेम',
    'footer.features': 'फीचर्स',
    'footer.whyCompression': 'संपीड़न क्यों?',
    'footer.company': 'कंपनी',
    'footer.caseStudies': 'केस स्टडीज',
    'footer.globalImpact': 'वैश्विक प्रभाव',
    'footer.partnerships': 'साझेदारी',
    'footer.sdgs': 'SDGs',
    'footer.ourBlogs': 'हमारे ब्लॉग',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'API डैशबोर्ड',
    'footer.developerProgram': 'डेवलपर प्रोग्राम',
    'footer.apiPricing': 'API मूल्य निर्धारण',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'संसाधन',
    'footer.documentation': 'दस्तावेज़ीकरण',
    'footer.copyright': '© 2025 SlimFile. संपीड़ित करें • परिवर्तित करें • सरल बनाएं',

    // FileUpload
    'fileUpload.supportText': '1GB तक की PDF, इमेज, PPTX, DOCX और XLSX फ़ाइलों के लिए समर्थन',
    'fileUpload.dropFiles': 'अपनी फ़ाइलें यहाँ छोड़ें या ब्राउज़ करने के लिए क्लिक करें',
    'fileUpload.dragDropText': '10 फ़ाइलों तक चुनने के लिए खींचें और छोड़ें या क्लिक करें',
    'fileUpload.errorMaxFiles': 'आप एक बार में 10 फ़ाइलें तक अपलोड कर सकते हैं।',
    'fileUpload.errorInvalidType': 'कृपया केवल PDF, JPEG, PNG, WebP, PPTX, DOCX, या XLSX फ़ाइलें चुनें।',
    'fileUpload.errorOldPpt': 'कृपया अपलोड करने से पहले अपनी प्रस्तुति को .pptx फ़ाइल के रूप में सहेजें।',
    'fileUpload.errorOldDoc': 'कृपया अपलोड करने से पहले अपने दस्तावेज़ को .docx फ़ाइल के रूप में सहेजें।',
    'fileUpload.errorOldXls': 'कृपया अपलोड करने से पहले अपनी स्प्रेडशीट को .xlsx फ़ाइल के रूप में सहेजें।',
    'fileUpload.errorFileSize': 'प्रत्येक फ़ाइल 1GB से कम होनी चाहिए।',
  },

  ru: {
    // Header
    'header.home': 'Главная',
    'header.features': 'Функции',
    'header.pricing': 'Цены',
    'header.blog': 'Блог',
    'header.login': 'Войти',
    'header.signup': 'Регистрация',
    'header.dashboard': 'Панель',
    'header.logout': 'Выйти',

    // Hero Section
    'hero.title': 'Сжимать. Конвертировать. Упрощать',
    'hero.subtitle': 'Все ваши инструменты для работы с файлами в одном месте',
    'hero.cta': 'Начать',
    'hero.learnMore': 'Узнать Больше',

    // File Upload
    'upload.title': 'Загрузите Ваши Файлы',
    'upload.dragDrop': 'Перетащите файлы сюда',
    'upload.or': 'или',
    'upload.browse': 'Обзор Файлов',
    'upload.support': 'Поддержка файлов PDF, изображений, PPTX, DOCX и XLSX до 1GB',

    // Compression
    'compress.title': 'Сжать Файлы',
    'compress.subtitle': 'Уменьшите размер файла без потери качества',
    'compress.start': 'Начать Сжатие',
    'compress.complete': 'Сжатие Завершено!',
    'compress.downloading': 'Загрузка...',
    'compress.download': 'Скачать',
    'compress.share': 'Поделиться',
    'compress.reset': 'Сжать Ещё',
    'compress.heroTitle': 'Сжать Файлы',
    'compress.heroSubtitle': 'Мгновенно',
    'compress.heroDescription': 'Уменьшайте размер файлов без ущерба для качества. Поддержка изображений, PDF и документов Office с',
    'compress.lightningFast': 'молниеносной обработкой',
    'compress.feature.fast': 'Молниеносно',
    'compress.feature.fastDesc': 'Сжатие за секунды',
    'compress.feature.secure': '100% Безопасно',
    'compress.feature.secureDesc': 'Обработка на стороне клиента',
    'compress.feature.available': 'Всегда Доступно',
    'compress.feature.availableDesc': 'Сжатие 24/7',
    'compress.formatsTitle': 'Поддерживаемые Форматы Сжатия',
    'compress.imageCompression': 'Сжатие Изображений',
    'compress.documentCompression': 'Сжатие Документов',
    'compress.howItWorks': 'Как Это Работает',
    'compress.step1': 'Загрузите Файлы',
    'compress.step1Desc': 'Выберите или перетащите ваши файлы',
    'compress.step2': 'Сжатие',
    'compress.step2Desc': 'Мы оптимизируем ваши файлы мгновенно',
    'compress.step3': 'Скачайте',
    'compress.step3Desc': 'Получите сжатые файлы',
    'compress.uploadTitle': 'Загрузите Ваши Файлы',
    'compress.resultsTitle': 'Результаты Сжатия',
    'compress.uploadSubtitle': 'Перетащите файлы или нажмите для обзора',
    'compress.processingSubtitle': 'Ваши файлы сжимаются',
    'compress.whyChoose': 'Почему SlimFile?',
    'compress.quality': 'Высокое Качество',
    'compress.qualityDesc': 'Сохраняйте отличное качество при уменьшении размера',
    'compress.allTypes': 'Все Типы Файлов',
    'compress.allTypesDesc': 'Поддержка изображений, PDF и файлов PPTX',
    'compress.batch': 'Пакетная Обработка',
    'compress.batchDesc': 'Сжимайте несколько файлов одновременно',
    'compress.noInstall': 'Без Установки',
    'compress.noInstallDesc': 'Работает прямо в браузере',
    'compress.almostDone': 'Почти Готово',
    'compress.hangInThere': 'Подождите... завершаем сжатие!',
    'compress.notice': 'Уведомление о Сжатии',
    'compress.failed': 'Сжатие Не Удалось',
    'compress.failedDesc': 'Произошла ошибка при сжатии файла. Пожалуйста, попробуйте снова.',
    'compress.sizeReduced': 'Размер уменьшен на',
    'compress.loginSuccess': 'Вход Выполнен Успешно',
    'compress.recompressPrompt': 'Пожалуйста, сжмите файлы заново для загрузки.',

    // Conversion
    'convert.title': 'Конвертировать Файлы',
    'convert.subtitle': 'Преобразуйте файлы между форматами',
    'convert.start': 'Начать Конвертацию',
    'convert.complete': 'Конвертация Завершена!',
    'convert.selectFormat': 'Выбрать Формат',
    'convert.convertTo': 'Конвертировать в',
    'convert.heroTitle': 'Конвертировать Файлы',
    'convert.heroSubtitle': 'Легко',
    'convert.heroDescription': 'Преобразуйте файлы между форматами с идеальным качеством. Поддержка изображений, PDF и документов Office с',
    'convert.converting': 'Конвертация...',
    'convert.successDesc': 'Файл успешно конвертирован в',
    'convert.feature.fast': 'Молниеносно',
    'convert.feature.fastDesc': 'Конвертация за секунды',
    'convert.feature.quality': 'Идеальное Качество',
    'convert.feature.qualityDesc': 'Сохраняйте отличное качество при конвертации',
    'convert.feature.secure': '100% Безопасно',
    'convert.feature.secureDesc': 'Обработка на стороне клиента',
    'convert.formatsTitle': 'Поддерживаемые Конвертации',
    'convert.images': 'Изображения',
    'convert.documents': 'Документы',
    'convert.howItWorks': 'Как Это Работает',
    'convert.step1': 'Загрузите Файлы',
    'convert.step1Desc': 'Выберите или перетащите ваши файлы',
    'convert.step2': 'Конвертация',
    'convert.step2Desc': 'Мы преобразуем ваши файлы мгновенно',
    'convert.step3': 'Скачайте',
    'convert.step3Desc': 'Получите конвертированные файлы',
    'convert.whyChoose': 'Почему SlimFile?',
    'convert.multiFormat': 'Мульти-Формат',
    'convert.multiFormatDesc': 'Поддержка нескольких форматов файлов',
    'convert.highQuality': 'Высокое Качество',
    'convert.highQualityDesc': 'Сохраняйте отличное качество при конвертации',
    'convert.batchConvert': 'Пакетная Конвертация',
    'convert.batchConvertDesc': 'Конвертируйте несколько файлов одновременно',
    'convert.noInstall': 'Без Установки',
    'convert.noInstallDesc': 'Работает прямо в браузере',

    // Convert & Compress
    'convertCompress.heroTitle': 'Конвертировать и Сжать',
    'convertCompress.heroSubtitle': 'Всё в Одном',
    'convertCompress.heroDescription': 'Преобразуйте файлы между форматами и оптимизируйте их размер за один шаг. Идеальное качество с',
    'convertCompress.selectFormat': 'Выбрать Формат',
    'convertCompress.convertTo': 'Конвертировать в',
    'convertCompress.converting': 'Конвертация...',
    'convertCompress.compressing': 'Сжатие...',
    'convertCompress.complete': 'Завершено',
    'convertCompress.successDesc': 'Файл конвертирован в',
    'convertCompress.feature.efficient': 'Эффективно',
    'convertCompress.feature.efficientDesc': 'Конвертация и сжатие вместе',
    'convertCompress.feature.quality': 'Идеальное Качество',
    'convertCompress.feature.qualityDesc': 'Сохраняйте отличное качество при конвертации и сжатии',
    'convertCompress.feature.timeSaving': 'Экономия Времени',
    'convertCompress.feature.timeSavingDesc': 'Обработка за один шаг',
    'convertCompress.formatsTitle': 'Поддерживаемые Форматы',
    'convertCompress.images': 'Изображения',
    'convertCompress.documents': 'Документы',
    'convertCompress.howItWorks': 'Как Это Работает',
    'convertCompress.step1': 'Загрузите Файлы',
    'convertCompress.step1Desc': 'Выберите или перетащите ваши файлы',
    'convertCompress.step2': 'Конвертация',
    'convertCompress.step2Desc': 'Мы преобразуем файлы в желаемый формат',
    'convertCompress.step3': 'Сжатие',
    'convertCompress.step3Desc': 'Мы оптимизируем размер файла',
    'convertCompress.step4': 'Скачайте',
    'convertCompress.step4Desc': 'Получите конвертированные и сжатые файлы',
    'convertCompress.whyChoose': 'Почему SlimFile?',
    'convertCompress.twoInOne': 'Два в Одном',
    'convertCompress.twoInOneDesc': 'Конвертация и сжатие за один шаг',
    'convertCompress.maxCompression': 'Максимальное Сжатие',
    'convertCompress.maxCompressionDesc': 'Получите наилучший возможный размер файла',
    'convertCompress.smartOptimization': 'Умная Оптимизация',
    'convertCompress.smartOptimizationDesc': 'Автоматические алгоритмы для лучших результатов',
    'convertCompress.oneClick': 'Один Клик',
    'convertCompress.oneClickDesc': 'Простая и быстрая обработка',

    // Common
    'common.processing': 'Обработка...',
    'common.cancel': 'Отменить',
    'common.save': 'Сохранить',
    'common.close': 'Закрыть',
    'common.error': 'Ошибка',
    'common.success': 'Успех',
    'common.loading': 'Загрузка...',
    'common.saveToCloud': 'Сохранить в Облако',

    // Footer
    'footer.about': 'О Нас',
    'footer.contact': 'Контакты',
    'footer.privacy': 'Политика Конфиденциальности',
    'footer.terms': 'Условия Использования',
    'footer.rights': 'Все права защищены',
    'footer.compressionTools': 'Инструменты Сжатия',
    'footer.compressPdf': 'Сжать PDF Онлайн',
    'footer.compressPptx': 'Сжать PPTX Онлайн',
    'footer.compressImages': 'Сжать Изображения Онлайн',
    'footer.compressPdfEmail': 'Сжать PDF для Email',
    'footer.ourBlog': 'Наши Статьи в Блоге',
    'footer.viewAll': 'Посмотреть Всё',
    'footer.pdfGuides': 'Руководства по PDF',
    'footer.imageOptimization': 'Оптимизация Изображений',
    'footer.documentMedia': 'Документы и Медиа',
    'footer.performanceStrategy': 'Производительность и Стратегия',
    'footer.quickAccess': 'Быстрый Доступ',
    'footer.product': 'Продукт',
    'footer.compress': 'Сжатие',
    'footer.convertOnly': 'Только Конвертация',
    'footer.convertCompress': 'Конвертация и Сжатие',
    'footer.game': 'Игра',
    'footer.features': 'Функции',
    'footer.whyCompression': 'Зачем Сжатие?',
    'footer.company': 'Компания',
    'footer.caseStudies': 'Примеры Использования',
    'footer.globalImpact': 'Глобальное Влияние',
    'footer.partnerships': 'Партнёрства',
    'footer.sdgs': 'ЦУР',
    'footer.ourBlogs': 'Наши Блоги',
    'footer.slimfileApi': 'SlimFile API',
    'footer.apiDashboard': 'Панель API',
    'footer.developerProgram': 'Программа для Разработчиков',
    'footer.apiPricing': 'Цены на API',
    'footer.stepsbuild': 'STEPsBuild',
    'footer.resources': 'Ресурсы',
    'footer.documentation': 'Документация',
    'footer.copyright': '© 2025 SlimFile. Сжимать • Конвертировать • Упрощать',

    // FileUpload
    'fileUpload.supportText': 'Поддержка файлов PDF, изображений, PPTX, DOCX и XLSX до 1GB',
    'fileUpload.dropFiles': 'Перетащите файлы сюда или нажмите для обзора',
    'fileUpload.dragDropText': 'Перетащите или нажмите для выбора до 10 файлов',
    'fileUpload.errorMaxFiles': 'Вы можете загрузить до 10 файлов за раз.',
    'fileUpload.errorInvalidType': 'Пожалуйста, выберите только файлы PDF, JPEG, PNG, WebP, PPTX, DOCX или XLSX.',
    'fileUpload.errorOldPpt': 'Пожалуйста, сохраните презентацию как файл .pptx перед загрузкой.',
    'fileUpload.errorOldDoc': 'Пожалуйста, сохраните документ как файл .docx перед загрузкой.',
    'fileUpload.errorOldXls': 'Пожалуйста, сохраните таблицу как файл .xlsx перед загрузкой.',
    'fileUpload.errorFileSize': 'Каждый файл должен быть меньше 1GB.',
  },
};

/**
 * Get the current language from localStorage
 */
export function getCurrentLanguage(): Language {
  const stored = localStorage.getItem('slimfile-language');
  if (stored && isValidLanguage(stored)) {
    return stored as Language;
  }

  // Try to detect browser language
  const browserLang = navigator.language.split('-')[0];
  if (isValidLanguage(browserLang)) {
    return browserLang as Language;
  }

  return 'en'; // Default to English
}

/**
 * Set the current language
 */
export function setLanguage(language: Language): void {
  localStorage.setItem('slimfile-language', language);
  // Trigger a custom event so components can react
  window.dispatchEvent(new CustomEvent('languagechange', { detail: language }));
}

/**
 * Check if a language code is valid
 */
function isValidLanguage(code: string): boolean {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === code);
}

/**
 * Get a translation by key
 */
export function t(key: string, language?: Language): string {
  const lang = language || getCurrentLanguage();
  return translations[lang][key] || translations.en[key] || key;
}

/**
 * Get language info by code
 */
export function getLanguageInfo(code: Language): LanguageOption | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}
